import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import TableRow from '@tiptap/extension-table-row';
import { SlhTable, SlhTableCell, SlhTableHeader } from './tiptapTable';
import { FontFamily } from '@tiptap/extension-font-family';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import { CellSelection } from '@tiptap/pm/tables';
import { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ImageLibraryModal } from "./ImageLibraryModal";
import ImageEditorModal from "./ImageEditorModal";
import { ImagePresetsRow } from "./ImagePresetsRow";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { 
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code, 
  Heading1, Heading2, Heading3, List, ListOrdered, AlignLeft, AlignCenter, 
  AlignRight, AlignJustify, Image as ImageIcon, Link as LinkIcon, 
  FileCode, Undo, Redo, Video, Maximize2, SlidersHorizontal,
  Shapes, Info, AlertTriangle, CheckCircle2, Lightbulb, Minus,
  Square, Circle, ArrowRight, Slash, Star, Diamond, Code2, Settings2,
  Baseline, Highlighter, Table as TableIcon, Type as FontIcon, Pilcrow,
  MapPin, CalendarClock, LayoutTemplate, MousePointerClick, Crop as CropIcon
} from "lucide-react";
import axios from 'axios';
import { toast } from 'sonner';
import { Callout, FancyDivider, InlineShape, VideoEmbed, HtmlEmbed, CtaButton, getVideoEmbedSrc, genEmbedId } from "./tiptapShapes";
import { isSafeCtaUrl } from "../ui/RichHtml";
import { Upload as UploadIcon } from "lucide-react";
import { PALETTE_FIELDS } from "../ui/ColorPickerInput";
import { GRADIENT_DIRECTIONS, buildGradientCss } from "@/utils/gradientOverlay";
import { useSiteStyles } from "@/hooks/useSiteStyles";

// ---------------------------------------------------------------------------
// Rich shape palette — plain HTML snippets that Tiptap `insertContent`s
// verbatim. Every snippet is CSS-styled via `.slh-callout*`, `.slh-hr-*`
// and `.slh-shape` rules in App.css so it renders identically inside the
// editor (`.tiptap-editor-wrapper`) and on the public site
// (`[data-rich-html="true"]`) via `RichHtml`.
// ---------------------------------------------------------------------------
const SHAPE_CALLOUTS = [
  { key: "info",    label: "Info",    icon: Info,          emoji: "ℹ️" },
  { key: "warning", label: "Warning", icon: AlertTriangle, emoji: "⚠️" },
  { key: "success", label: "Success", icon: CheckCircle2, emoji: "✅" },
  { key: "tip",     label: "Tip",     icon: Lightbulb,     emoji: "💡" },
];

const SHAPE_DIVIDERS = [
  { key: "dotted",  label: "Dotted",  icon: Minus },
  { key: "wavy",    label: "Wavy",    icon: Slash },
  { key: "diamond", label: "Diamond", icon: Diamond },
  { key: "star",    label: "Stars",   icon: Star },
];

const SHAPE_GEO = [
  { key: "rectangle", label: "Rectangle", icon: Square },
  { key: "circle",    label: "Circle",    icon: Circle },
  { key: "arrow",     label: "Arrow",     icon: ArrowRight },
  { key: "line",      label: "Line",      icon: Slash },
  { key: "star",      label: "Star",      icon: Star },
  { key: "diamond",   label: "Diamond",   icon: Diamond },
];

const IMAGE_SHADOW_PRESETS = {
  none: "",
  soft: "0 2px 8px rgba(0,0,0,0.12)",
  medium: "0 6px 16px rgba(0,0,0,0.18)",
  strong: "0 12px 28px rgba(0,0,0,0.3)",
};

// Shared insert helper for atom/block nodes added via toolbar buttons
// (Shapes, HTML Embed). If the current selection is a NodeSelection (e.g.
// the admin just clicked an existing image, video or HTML embed), a plain
// insertContent() REPLACES that selected node — so when one is selected we
// insert right after it instead.
// Applies horizontal/vertical content alignment to every cell covered by
// the current selection — a dragged multi-cell CellSelection updates all
// of them at once, a plain cursor position updates just the enclosing
// <td>/<th>.
function applyCellAlignment(editor, attrs) {
  if (!editor) return;
  const { state, view } = editor;
  const { selection } = state;
  const tr = state.tr;
  let changed = false;

  if (selection instanceof CellSelection) {
    selection.forEachCell((cell, pos) => {
      tr.setNodeMarkup(pos, null, { ...cell.attrs, ...attrs });
      changed = true;
    });
  } else {
    const { $from } = selection;
    for (let d = $from.depth; d > 0; d--) {
      const node = $from.node(d);
      if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
        tr.setNodeMarkup($from.before(d), null, { ...node.attrs, ...attrs });
        changed = true;
        break;
      }
    }
  }

  if (changed) {
    view.dispatch(tr);
    view.focus();
  }
}

// Small inline palette swatch grid for the Table Properties dialog
// (background/border colour) — same restricted Site Styles palette as
// PaletteColorPopover, but rendered directly (not behind its own trigger)
// since it already lives inside a dialog. Includes a "None" option, unlike
// the toolbar popover which has a separate "Remove colour" link instead.
function TablePaletteSwatches({ value, onChange, label, testId }) {
  const { styles: siteStyles } = useSiteStyles();
  const seenHex = new Set();
  const palette = PALETTE_FIELDS
    .map(({ field, label: swatchLabel }) => ({ hex: siteStyles?.[field], label: swatchLabel }))
    .filter((c) => {
      if (!c.hex) return false;
      const key = c.hex.toLowerCase();
      if (seenHex.has(key)) return false;
      seenHex.add(key);
      return true;
    });
  const activeHex = (value || "").toLowerCase();

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex flex-wrap gap-1.5" data-testid={testId}>
        <button
          type="button"
          onClick={() => onChange("")}
          className={`h-7 w-7 rounded-sm border flex items-center justify-center text-[10px] text-muted-foreground bg-[repeating-linear-gradient(45deg,#f3f4f6,#f3f4f6_4px,#fff_4px,#fff_8px)] ${
            !value ? "ring-2 ring-primary ring-offset-1 border-primary" : "border-input"
          }`}
          title="None"
          data-testid={`${testId}-none`}
        >
          ×
        </button>
        {palette.map(({ hex, label: swatchLabel }) => (
          <button
            key={swatchLabel}
            type="button"
            onClick={() => onChange(hex)}
            className={`h-7 w-7 rounded-sm border shadow-sm hover:scale-110 transition-all flex-shrink-0 ${
              activeHex === hex.toLowerCase() ? "ring-2 ring-primary ring-offset-1 border-primary" : "border-input"
            }`}
            style={{ backgroundColor: hex }}
            title={`${swatchLabel} (${hex})`}
            data-testid={`${testId}-${swatchLabel.toLowerCase().replace(/\s+/g, "-")}`}
          />
        ))}
      </div>
    </div>
  );
}

// Single "Table" toolbar button that doubles as Insert (no table under the
// cursor) and Edit (cursor inside a table) — keeps the already-packed
// toolbar from needing a separate button per table action.
function TableToolbarPopover({ editor, onOpenProperties, onOpenCellAlign }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState('3');
  const [cols, setCols] = useState('3');
  const [withHeader, setWithHeader] = useState(true);
  // editor.isActive('table') computed at *render* time can be stale —
  // this component only re-renders on its own state changes or a parent
  // re-render, neither of which fires on a pure cursor move (clicking into
  // a different cell doesn't touch any React state). Re-check right when
  // the popover opens instead, so it always reflects the live selection.
  const [inTable, setInTable] = useState(false);

  const doInsert = () => {
    const r = Math.max(1, Math.min(20, parseInt(rows, 10) || 3));
    const c = Math.max(1, Math.min(10, parseInt(cols, 10) || 3));
    const chain = editor.chain().focus();
    const { selection } = editor.state;
    // insertTable() replaces the current selection outright — if an
    // image/video/embed is NodeSelected, collapse to a text position right
    // after it first so the table is inserted alongside it, not over it.
    if (selection && selection.node) {
      chain.setTextSelection(selection.to);
    }
    chain.insertTable({ rows: r, cols: c, withHeaderRow: withHeader }).run();
    setOpen(false);
  };

  // Resets every column's drag-resized `colwidth` back to null so the
  // table reverts to an even split across its columns (table-layout:
  // fixed + no explicit widths = equal distribution).
  const doEqualizeColumns = () => {
    const { state } = editor;
    let tablePos = null;
    let tableNode = null;
    state.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos) => {
      if (node.type.name === 'table') {
        tablePos = pos;
        tableNode = node;
      }
    });
    if (!tableNode) return;
    let tr = state.tr;
    let changed = false;
    tableNode.descendants((node, pos) => {
      if ((node.type.name === 'tableCell' || node.type.name === 'tableHeader') && node.attrs.colwidth) {
        tr = tr.setNodeMarkup(tablePos + 1 + pos, undefined, { ...node.attrs, colwidth: null });
        changed = true;
      }
    });
    if (changed) editor.view.dispatch(tr);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { if (o) setInTable(editor.isActive('table')); setOpen(o); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0"
          title="Table"
          data-testid="tiptap-table-trigger"
        >
          <TableIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3 space-y-3" data-testid="tiptap-table-popover">
        {inTable ? (
          <>
            <p className="text-xs font-medium text-muted-foreground">Edit Table</p>
            <div className="grid grid-cols-2 gap-1.5">
              <Button type="button" size="sm" variant="outline" className="rounded-none text-xs h-8" onClick={() => editor.chain().focus().addRowBefore().run()} data-testid="tiptap-table-add-row-above">Add Row Above</Button>
              <Button type="button" size="sm" variant="outline" className="rounded-none text-xs h-8" onClick={() => editor.chain().focus().addRowAfter().run()} data-testid="tiptap-table-add-row-below">Add Row Below</Button>
              <Button type="button" size="sm" variant="outline" className="rounded-none text-xs h-8" onClick={() => editor.chain().focus().addColumnBefore().run()} data-testid="tiptap-table-add-col-before">Add Column Before</Button>
              <Button type="button" size="sm" variant="outline" className="rounded-none text-xs h-8" onClick={() => editor.chain().focus().addColumnAfter().run()} data-testid="tiptap-table-add-col-after">Add Column After</Button>
              <Button type="button" size="sm" variant="outline" className="rounded-none text-xs h-8" onClick={() => editor.chain().focus().deleteRow().run()} data-testid="tiptap-table-delete-row">Delete Row</Button>
              <Button type="button" size="sm" variant="outline" className="rounded-none text-xs h-8" onClick={() => editor.chain().focus().deleteColumn().run()} data-testid="tiptap-table-delete-col">Delete Column</Button>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-full rounded-none text-xs"
              onClick={() => { editor.chain().focus().run(); doEqualizeColumns(); }}
              title="Reset any manually-dragged column widths back to an even split"
              data-testid="tiptap-table-equalize-columns"
            >
              Equal Column Widths
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              className="w-full rounded-none"
              onClick={() => { editor.chain().focus().deleteTable().run(); setOpen(false); }}
              data-testid="tiptap-table-delete-table"
            >
              Delete Table
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-full rounded-none"
              onClick={() => { setOpen(false); onOpenCellAlign(); }}
              data-testid="tiptap-table-open-cell-align"
            >
              Cell Alignment…
            </Button>
            <Button
              type="button"
              size="sm"
              className="w-full rounded-none"
              onClick={() => { setOpen(false); onOpenProperties(); }}
              data-testid="tiptap-table-open-properties"
            >
              Table Properties…
            </Button>
          </>
        ) : (
          <>
            <p className="text-xs font-medium text-muted-foreground">Insert Table</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Rows</Label>
                <Input type="number" min="1" max="20" value={rows} onChange={(e) => setRows(e.target.value)} className="rounded-none h-8" data-testid="tiptap-table-rows-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Columns</Label>
                <Input type="number" min="1" max="10" value={cols} onChange={(e) => setCols(e.target.value)} className="rounded-none h-8" data-testid="tiptap-table-cols-input" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={withHeader} onChange={(e) => setWithHeader(e.target.checked)} data-testid="tiptap-table-header-checkbox" />
              Include header row
            </label>
            <Button type="button" size="sm" className="w-full rounded-none" onClick={doInsert} data-testid="tiptap-table-insert-btn">
              Insert Table
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Ready-made embed snippets — one input away from a working iframe, so
// admins don't have to hand-write HTML for the two most common embeds.
// Inserted as the same `htmlEmbed` node the free-form "Embed HTML" button
// uses, so they're resizable/floatable the same way.
const EMBED_TEMPLATES = [
  {
    key: "map",
    label: "Google Map",
    icon: MapPin,
    inputLabel: "Address or postcode",
    placeholder: "e.g. 10 Downing Street, London SW1A 2AA",
    helper: "We'll embed a live Google Map centered on this address — no API key needed.",
  },
  {
    key: "calendar",
    label: "Google Calendar",
    icon: CalendarClock,
    inputLabel: "Calendar ID or embed URL",
    placeholder: "e.g. you@gmail.com, or a full embed URL",
    helper: 'From Google Calendar → Settings → your calendar → "Integrate calendar".',
  },
];

function buildEmbedTemplateHtml(kind, value) {
  let val = (value || "").trim();
  if (!val) return "";
  // The admin may paste a full <iframe ...> snippet straight from the
  // provider (e.g. Google Calendar's "Integrate calendar" box) instead of
  // just the bare ID/URL — pull the src out so it doesn't get double
  // URL-encoded into a broken link below.
  const iframeSrcMatch = val.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i);
  if (iframeSrcMatch) val = iframeSrcMatch[1];
  if (kind === "map") {
    const src = /^https?:\/\//i.test(val)
      ? val
      : `https://www.google.com/maps?q=${encodeURIComponent(val)}&output=embed`;
    return `<iframe src="${src}" width="100%" height="400" style="border:0;" loading="lazy" allowfullscreen referrerpolicy="no-referrer-when-downgrade"></iframe>`;
  }
  if (kind === "calendar") {
    const src = /^https?:\/\//i.test(val)
      ? val
      : `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(val)}&ctz=Europe%2FLondon`;
    return `<iframe src="${src}" width="100%" height="500" frameborder="0" scrolling="no" style="border:0;"></iframe>`;
  }
  return "";
}

function EmbedTemplatesPopover({ onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 px-3"
          title="Insert a ready-made embed — map, calendar"
          data-testid="tiptap-embed-templates-trigger"
        >
          <LayoutTemplate className="h-4 w-4 mr-1" />
          Templates
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-2 space-y-1" data-testid="tiptap-embed-templates-popover">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-1 mb-1">Ready-made embeds</p>
        {EMBED_TEMPLATES.map(({ key, label, icon: Icon }) => (
          <Button
            key={key}
            type="button"
            size="sm"
            variant="outline"
            className="w-full justify-start gap-2 text-xs h-9 rounded-none"
            onClick={() => { setOpen(false); onPick(key); }}
            data-testid={`tiptap-embed-template-${key}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </Button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function insertAfterSelection(editor, content) {
  const { selection } = editor.state;
  if (selection && selection.node) {
    editor.chain().focus().insertContentAt(selection.to, content).run();
  } else {
    editor.chain().focus().insertContent(content).run();
  }
}

// Removes genuinely-empty trailing paragraph(s) — e.g. the cursor-placement
// paragraph auto-inserted after a CTA/image/embed so the admin has
// somewhere to click and keep typing. Only strips from the very end, so an
// intentional blank line authored in the middle of the content (used for
// spacing) is left untouched. Only run on blur/finalise, never mid-typing,
// so it doesn't yank away the paragraph the admin is about to type into.
function stripTrailingEmptyParagraphs(html) {
  let result = html || "";
  const trailingEmptyP = /<p(?:\s[^>]*)?>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*$/i;
  while (trailingEmptyP.test(result)) {
    result = result.replace(trailingEmptyP, "");
  }
  return result;
}

// Text Color / Background Color toolbar buttons — restricted to the Site
// Styles Colour Palette (Admin -> Styles) so authored pages stay on-brand,
// same restriction the page-builder's own ColorPickerInput enforces.
// Font Family toolbar button — restricted to the 3 fonts defined in Site
// Styles (Admin -> Styles -> Typography), same "stay on-brand" restriction
// as the colour pickers above.
const FONT_FIELDS = [
  { field: "headingFont", label: "Heading Font", cssVar: "--font-heading" },
  { field: "bodyFont", label: "Normal Font", cssVar: "--font-body", sizeVar: "--base-font-size" },
  { field: "navFont", label: "Nav Font", cssVar: "--font-nav" },
  { field: "listFont", label: "List Font", cssVar: "--font-list", sizeVar: "--list-font-size" },
];

function FontFamilyPopover({ editor }) {
  const { styles: siteStyles } = useSiteStyles();
  const [open, setOpen] = useState(false);
  // Values applied are live `var(--font-heading)` etc. references, not the
  // resolved literal font name — so a saved piece of content keeps
  // tracking whatever an admin later changes in Site Styles, instead of
  // freezing in whatever font/size happened to be active the moment it
  // was clicked.
  const fonts = FONT_FIELDS
    .filter(({ field }) => !!siteStyles?.[field])
    .map(({ field, label, cssVar, sizeVar }) => ({
      field,
      label,
      resolvedName: siteStyles[field],
      value: `var(${cssVar})`,
      fontSize: sizeVar ? `var(${sizeVar})` : null,
    }));
  const activeFont = editor.getAttributes('textStyle').fontFamily;
  // Clicking into the popover moves DOM focus off the editor, which would
  // otherwise collapse the text selection before the font gets a chance to
  // apply — preventDefault on mousedown keeps the original selection intact.
  const keepSelection = (e) => e.preventDefault();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 px-2 gap-1 w-[120px] justify-start"
          title="Font Family"
          data-testid="tiptap-font-family-trigger"
          onMouseDown={keepSelection}
        >
          <FontIcon className="h-4 w-4 flex-shrink-0" />
          <span className="text-xs max-w-[90px] truncate">{activeFont ? (FONT_FIELDS.find(f => `var(${f.cssVar})` === activeFont)?.label || "Custom") : "Font"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3 space-y-1" data-testid="tiptap-font-family-popover" onOpenAutoFocus={(e) => e.preventDefault()}>
        <p className="text-xs font-medium text-muted-foreground mb-1">Site Styles fonts</p>
        {fonts.length === 0 && (
          <p className="text-xs text-muted-foreground">No fonts set in Admin → Styles yet.</p>
        )}
        {fonts.map(({ field, label, resolvedName, value, fontSize }) => (
          <button
            key={field}
            type="button"
            onMouseDown={keepSelection}
            onClick={() => {
              const chain = editor.chain().focus().setFontFamily(value);
              (fontSize ? chain.setMark('textStyle', { fontSize }) : chain).run();
              setOpen(false);
            }}
            className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors flex items-center justify-between ${
              activeFont === value ? "bg-muted ring-1 ring-primary" : ""
            }`}
            style={{ fontFamily: `'${resolvedName}', sans-serif` }}
            data-testid={`tiptap-font-family-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span>{resolvedName}</span>
            <span className="text-[10px] text-muted-foreground">{label}</span>
          </button>
        ))}
        <button
          type="button"
          onMouseDown={keepSelection}
          onClick={() => { editor.chain().focus().unsetFontFamily().setMark('textStyle', { fontSize: null }).run(); setOpen(false); }}
          className="mt-2 text-xs text-muted-foreground hover:text-foreground underline"
          data-testid="tiptap-font-family-clear"
        >
          Remove font (use default)
        </button>
      </PopoverContent>
    </Popover>
  );
}

function PaletteColorPopover({ icon: Icon, title, testIdPrefix, onPick, onClear, activeColor }) {
  const { styles: siteStyles } = useSiteStyles();
  const [open, setOpen] = useState(false);
  const seenHex = new Set();
  const palette = PALETTE_FIELDS
    .map(({ field, label }) => ({ hex: siteStyles?.[field], label }))
    .filter((c) => {
      if (!c.hex) return false;
      const key = c.hex.toLowerCase();
      if (seenHex.has(key)) return false;
      seenHex.add(key);
      return true;
    });
  const activeHex = (activeColor || "").toLowerCase();
  // Same fix as the Font popover — without this, clicking into the
  // popover collapses the text selection before the colour can apply.
  const keepSelection = (e) => e.preventDefault();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0"
          title={title}
          data-testid={`${testIdPrefix}-trigger`}
          onMouseDown={keepSelection}
        >
          <Icon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start" data-testid={`${testIdPrefix}-popover`} onOpenAutoFocus={(e) => e.preventDefault()}>
        <p className="text-xs font-medium text-muted-foreground mb-2">{title} — Site Styles palette</p>
        <div className="flex flex-wrap gap-1.5">
          {palette.map(({ hex, label }) => (
            <button
              key={label}
              type="button"
              onMouseDown={keepSelection}
              onClick={() => { onPick(hex); setOpen(false); }}
              className={`h-7 w-7 rounded-sm border shadow-sm hover:scale-110 transition-all flex-shrink-0 ${
                activeHex === hex.toLowerCase() ? "ring-2 ring-primary ring-offset-1 border-primary" : ""
              }`}
              style={{ backgroundColor: hex }}
              title={`${label} (${hex})`}
              data-testid={`${testIdPrefix}-${label.toLowerCase().replace(/\s+/g, "-")}`}
            />
          ))}
          {palette.length === 0 && (
            <p className="text-xs text-muted-foreground">No palette colours set in Admin → Styles yet.</p>
          )}
        </div>
        <button
          type="button"
          onMouseDown={keepSelection}
          onClick={() => { onClear(); setOpen(false); }}
          className="mt-3 text-xs text-muted-foreground hover:text-foreground underline"
          data-testid={`${testIdPrefix}-clear`}
        >
          Remove {title.toLowerCase()}
        </button>
      </PopoverContent>
    </Popover>
  );
}

function ShapesToolbarButton({ editor }) {
  const [open, setOpen] = useState(false);
  const insertCallout = (variant, icon) => {
    // Single insertContent call (array form) — chaining two separate
    // insertContent calls replaces (deletes) the first inserted atom node,
    // since its insertion leaves a NodeSelection on it (same bug fixed for
    // the HTML Embed insert). insertAfterSelection also guards against
    // replacing an already-selected image/video/embed node.
    insertAfterSelection(editor, [
      {
        type: "callout",
        attrs: { variant, icon },
        content: [{ type: "paragraph", content: [{ type: "text", text: "Type your message…" }] }],
      },
      { type: "paragraph" },
    ]);
    setOpen(false);
  };
  const insertDivider = (variant) => {
    insertAfterSelection(editor, [
      { type: "fancyDivider", attrs: { variant } },
      { type: "paragraph" },
    ]);
    setOpen(false);
  };
  const insertShape = (shape) => {
    insertAfterSelection(editor, { type: "inlineShape", attrs: { shape } });
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 px-2 gap-1"
          title="Insert shape, callout or divider"
          data-testid="tiptap-shapes-trigger"
        >
          <Shapes className="h-4 w-4" />
          <span className="text-xs">Shapes</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3 space-y-3" data-testid="tiptap-shapes-popover">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Callouts</div>
          <div className="grid grid-cols-2 gap-1.5">
            {SHAPE_CALLOUTS.map(({ key, label, icon: Icon, emoji }) => (
              <Button
                key={key}
                type="button"
                size="sm"
                variant="outline"
                className="h-8 justify-start gap-1.5 text-xs"
                onClick={() => insertCallout(key, emoji)}
                data-testid={`tiptap-shape-callout-${key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Dividers</div>
          <div className="grid grid-cols-2 gap-1.5">
            {SHAPE_DIVIDERS.map(({ key, label, icon: Icon }) => (
              <Button
                key={key}
                type="button"
                size="sm"
                variant="outline"
                className="h-8 justify-start gap-1.5 text-xs"
                onClick={() => insertDivider(key)}
                data-testid={`tiptap-shape-divider-${key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Geometric</div>
          <div className="grid grid-cols-3 gap-1.5">
            {SHAPE_GEO.map(({ key, label, icon: Icon }) => (
              <Button
                key={key}
                type="button"
                size="sm"
                variant="outline"
                className="h-8 justify-start gap-1 text-xs"
                onClick={() => insertShape(key)}
                data-testid={`tiptap-shape-geo-${key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </Button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TiptapEditor({ value, onChange, placeholder }) {
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [showHtmlMode, setShowHtmlMode] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showCtaDialog, setShowCtaDialog] = useState(false);
  const [ctaText, setCtaText] = useState('Learn More');
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaTarget, setCtaTarget] = useState('same');
  const selectedCtaPosRef = useRef(null);
  const [showImageResize, setShowImageResize] = useState(false);
  const [imageWidth, setImageWidth] = useState('100');
  const [imageHeightPx, setImageHeightPx] = useState('');
  const imageNaturalRatioRef = useRef(1);
  const [imageOpacity, setImageOpacity] = useState('100');
  const [imageFloat, setImageFloat] = useState('none');
  const [imageJustify, setImageJustify] = useState('none');
  const [imageValign, setImageValign] = useState('none');
  const [imageRadius, setImageRadius] = useState('0');
  const [imageShadow, setImageShadow] = useState('none');
  const [imageBlur, setImageBlur] = useState('0');
  const [imageBrightness, setImageBrightness] = useState('100');
  const [imageGradient, setImageGradient] = useState('none');
  const [imageGradientColor, setImageGradientColor] = useState('#000000');
  const [imageGradientOpacity, setImageGradientOpacity] = useState('60');
  const [showImageCrop, setShowImageCrop] = useState(false);
  const [selectedImagePos, setSelectedImagePos] = useState(null);
  const [selectedImageSrc, setSelectedImageSrc] = useState('');
  const [showVideoResize, setShowVideoResize] = useState(false);
  const [videoWidth, setVideoWidth] = useState('100');
  const [videoFloat, setVideoFloat] = useState('none');
  const [videoDisplay, setVideoDisplay] = useState('inline');
  const [selectedVideoSrc, setSelectedVideoSrc] = useState('');
  const [showHtmlEmbedDialog, setShowHtmlEmbedDialog] = useState(false);
  const [htmlEmbedCode, setHtmlEmbedCode] = useState('');
  const [showEmbedTemplateDialog, setShowEmbedTemplateDialog] = useState(false);
  const [embedTemplateKind, setEmbedTemplateKind] = useState(null);
  const [embedTemplateValue, setEmbedTemplateValue] = useState('');
  const [showHtmlEmbedResize, setShowHtmlEmbedResize] = useState(false);
  const [htmlEmbedWidth, setHtmlEmbedWidth] = useState('100');
  const [htmlEmbedFloat, setHtmlEmbedFloat] = useState('none');
  const [selectedHtmlEmbedId, setSelectedHtmlEmbedId] = useState('');
  const htmlFileInputRef = useRef(null);
  const [showTablePropsDialog, setShowTablePropsDialog] = useState(false);
  const [tableWidthMode, setTableWidthMode] = useState('percent');
  const [tableWidthValue, setTableWidthValue] = useState('100');
  const [tableAlign, setTableAlign] = useState('left');
  const [tableBgColor, setTableBgColor] = useState('');
  const [tableBorderColor, setTableBorderColor] = useState('#d1d5db');
  const [tableBorderWidth, setTableBorderWidth] = useState('1');
  const [tableCellPadding, setTableCellPadding] = useState('');
  const [tableCellHeight, setTableCellHeight] = useState('');
  const [showCellAlignDialog, setShowCellAlignDialog] = useState(false);
  const [cellAlign, setCellAlign] = useState('left');
  const [cellValign, setCellValign] = useState('top');

  const lowlight = createLowlight(common);

  const editor = useEditor({
    // @tiptap/react v3 defaults this to false (perf optimisation) — but it
    // means toolbar buttons driven by editor.isActive()/getAttributes()
    // (Bold/Italic/Underline/Font Family/Text Color/etc) only refresh on a
    // document CHANGE, not on a plain cursor move or selection change, so
    // they silently go stale as soon as you click into already-formatted
    // text without typing. Needed for every isActive-driven toolbar button.
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({
        codeBlock: false, // Use CodeBlockLowlight instead
        link: false, // custom-configured Link below; avoids a duplicate-extension warning
        underline: false, // custom Underline below, same reason
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            fontSize: {
              default: null,
              parseHTML: (el) => el.style.fontSize || null,
              renderHTML: (attrs) => (attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {}),
            },
          };
        },
      }),
      Color,
      Highlight.configure({ multicolor: true }),
      FontFamily,
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: '100%',
            },
            height: {
              default: 'auto',
            },
            opacity: {
              default: '100',
            },
            float: {
              default: 'none',
            },
            // Block-level horizontal justification — independent of `float`
            // (which text-wraps). Only applied when float is 'none'; 'none'
            // here means "leave inline, no forced block/margin" so existing
            // content saved before this attribute existed renders unchanged.
            justify: {
              default: 'none',
            },
            // CSS vertical-align — lets an inline image line up with
            // surrounding text at its top/middle/bottom instead of the
            // browser's baseline default.
            valign: {
              default: 'none',
            },
            // Rounded corners, in px.
            borderRadius: {
              default: '0',
            },
            // Drop shadow preset key — 'none' | 'soft' | 'medium' | 'strong'.
            boxShadow: {
              default: 'none',
            },
            // Gaussian blur, in px — e.g. for a de-emphasised advert
            // background photo under overlay text.
            blur: {
              default: '0',
            },
            // CSS filter brightness(), in % — 100 = normal, <100 darkens,
            // >100 lightens. Independent of opacity (which fades toward
            // whatever sits behind it, not toward white/black).
            brightness: {
              default: '100',
            },
            // Colour "scrim" that fades from transparent to `gradientColor`,
            // commonly used to keep overlaid text legible on a photo.
            // 'none' | 'to-bottom' | 'to-top' | 'to-left' | 'to-right' | 'radial'.
            gradient: {
              default: 'none',
            },
            gradientColor: {
              default: '#000000',
            },
            gradientOpacity: {
              default: '60',
            },
          };
        },
        renderHTML({ HTMLAttributes }) {
          const {
            width, opacity, height, float, justify, valign, borderRadius, boxShadow, blur,
            brightness, gradient, gradientColor, gradientOpacity, src, alt, title,
          } = HTMLAttributes;

          const filterParts = [];
          if (blur && parseFloat(blur) > 0) filterParts.push(`blur(${blur}px)`);
          if (brightness && brightness !== '100') filterParts.push(`brightness(${parseInt(brightness, 10) / 100})`);
          const filterStyle = filterParts.length ? ` filter: ${filterParts.join(' ')};` : '';
          const opacityStyle = opacity && opacity !== '100' ? ` opacity: ${parseInt(opacity, 10) / 100};` : '';
          const gradientCss = buildGradientCss(gradient, gradientColor, gradientOpacity);

          if (!gradientCss) {
            // No gradient — original simple structure, unchanged from before.
            let style = `width: ${width || '100%'}; height: ${height || 'auto'};${opacityStyle}`;

            if (float === 'left') {
              style += ' float: left; margin: 0.25rem 1.5rem 1rem 0;';
            } else if (float === 'right') {
              style += ' float: right; margin: 0.25rem 0 1rem 1.5rem;';
            } else if (justify && justify !== 'none') {
              style += ' display: block;';
              if (justify === 'left') style += ' margin: 0.5rem auto 0.5rem 0;';
              else if (justify === 'center') style += ' margin: 0.5rem auto;';
              else if (justify === 'right') style += ' margin: 0.5rem 0 0.5rem auto;';
            }

            if (valign && valign !== 'none') style += ` vertical-align: ${valign};`;
            if (borderRadius && borderRadius !== '0') style += ` border-radius: ${borderRadius}px;`;
            if (boxShadow && IMAGE_SHADOW_PRESETS[boxShadow]) style += ` box-shadow: ${IMAGE_SHADOW_PRESETS[boxShadow]};`;
            style += filterStyle;

            const attrs = { src, alt: alt || '', title: title || '', style, class: 'editor-image' };
            if (justify && justify !== 'none') attrs['data-justify'] = justify;
            if (valign && valign !== 'none') attrs['data-valign'] = valign;
            if (boxShadow && boxShadow !== 'none') attrs['data-shadow'] = boxShadow;
            return ['img', attrs];
          }

          // Gradient overlay needs a wrapper (a single <img> can't paint
          // anything on top of its own photo content) — layout attrs that
          // used to live on the <img> move to this wrapper; the <img> just
          // fills it edge-to-edge.
          let wrapperStyle = `position: relative; width: ${width || '100%'}; height: ${height || 'auto'}; overflow: hidden;`;
          if (float === 'left') wrapperStyle += ' float: left; margin: 0.25rem 1.5rem 1rem 0;';
          else if (float === 'right') wrapperStyle += ' float: right; margin: 0.25rem 0 1rem 1.5rem;';
          else if (justify && justify !== 'none') {
            wrapperStyle += ' display: block;';
            if (justify === 'left') wrapperStyle += ' margin: 0.5rem auto 0.5rem 0;';
            else if (justify === 'center') wrapperStyle += ' margin: 0.5rem auto;';
            else if (justify === 'right') wrapperStyle += ' margin: 0.5rem 0 0.5rem auto;';
          } else {
            wrapperStyle += ' display: inline-block;';
          }
          if (valign && valign !== 'none') wrapperStyle += ` vertical-align: ${valign};`;
          if (borderRadius && borderRadius !== '0') wrapperStyle += ` border-radius: ${borderRadius}px;`;
          if (boxShadow && IMAGE_SHADOW_PRESETS[boxShadow]) wrapperStyle += ` box-shadow: ${IMAGE_SHADOW_PRESETS[boxShadow]};`;

          const wrapperAttrs = { class: 'editor-image-wrap', style: wrapperStyle, 'data-gradient': gradient };
          if (gradientColor) wrapperAttrs['data-gradient-color'] = gradientColor;
          if (gradientOpacity) wrapperAttrs['data-gradient-opacity'] = gradientOpacity;
          if (justify && justify !== 'none') wrapperAttrs['data-justify'] = justify;
          if (valign && valign !== 'none') wrapperAttrs['data-valign'] = valign;
          if (boxShadow && boxShadow !== 'none') wrapperAttrs['data-shadow'] = boxShadow;

          const innerImgStyle = `width: 100%; height: 100%; object-fit: cover; display: block;${opacityStyle}${filterStyle}`;

          return ['span', wrapperAttrs,
            ['img', { src, alt: alt || '', title: title || '', style: innerImgStyle, class: 'editor-image' }],
            ['span', { class: 'editor-image-gradient-overlay', style: `position: absolute; inset: 0; pointer-events: none; background: ${gradientCss};` }],
          ];
        },
        parseHTML() {
          return [{
            tag: 'img',
            getAttrs: dom => {
              // Layout attrs live on the gradient wrapper <span> when present,
              // otherwise directly on the <img> (pre-gradient saved content).
              const wrapper = dom.parentElement?.classList?.contains('editor-image-wrap') ? dom.parentElement : null;
              const source = wrapper || dom;
              const width = (wrapper ? wrapper.style.width : null) || dom.style.width || dom.getAttribute('width') || '100%';
              const heightStyle = (wrapper ? wrapper.style.height : null) || dom.style.height || '';
              const height = /^\d+(\.\d+)?px$/.test(heightStyle) ? heightStyle : 'auto';
              const opacity = dom.style.opacity ? Math.round(parseFloat(dom.style.opacity) * 100).toString() : '100';
              const float = source.style.float === 'left' || source.style.float === 'right' ? source.style.float : 'none';
              const justify = source.getAttribute('data-justify') || 'none';
              const valign = source.getAttribute('data-valign') || 'none';
              const radiusStyle = source.style.borderRadius || '';
              const borderRadius = /^\d+(\.\d+)?px$/.test(radiusStyle) ? parseInt(radiusStyle, 10).toString() : '0';
              const boxShadow = source.getAttribute('data-shadow') || 'none';
              const blurMatch = /blur\((\d+(?:\.\d+)?)px\)/.exec(dom.style.filter || '');
              const blur = blurMatch ? blurMatch[1] : '0';
              const brightMatch = /brightness\(([\d.]+)\)/.exec(dom.style.filter || '');
              const brightness = brightMatch ? Math.round(parseFloat(brightMatch[1]) * 100).toString() : '100';
              const gradient = (wrapper && wrapper.getAttribute('data-gradient')) || 'none';
              const gradientColor = (wrapper && wrapper.getAttribute('data-gradient-color')) || '#000000';
              const gradientOpacity = (wrapper && wrapper.getAttribute('data-gradient-opacity')) || '60';

              return {
                src: dom.getAttribute('src'),
                alt: dom.getAttribute('alt'),
                title: dom.getAttribute('title'),
                width,
                height,
                opacity,
                float,
                justify,
                valign,
                borderRadius,
                boxShadow,
                blur,
                brightness,
                gradient,
                gradientColor,
                gradientOpacity,
              };
            },
          }];
        },
      }).configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'editor-link',
        },
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
      Callout,
      FancyDivider,
      InlineShape,
      VideoEmbed,
      HtmlEmbed,
      CtaButton,
      SlhTable.configure({ resizable: true }),
      TableRow,
      SlhTableHeader,
      SlhTableCell,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    onBlur: ({ editor }) => {
      // Only updates the *exported* value — never calls `setContent()`
      // here. A trailing-node plugin (needed so there's always a cursor
      // spot after an atom like a CTA button) keeps re-adding an empty
      // paragraph to the *live* document on every transaction regardless,
      // so fighting it with setContent on every blur just resets the
      // selection for no lasting benefit (and broke things like the Table
      // menu's "am I inside a table" check, which runs after a blur).
      const cleaned = stripTrailingEmptyParagraphs(editor.getHTML());
      if (cleaned !== editor.getHTML()) {
        onChange(cleaned);
      }
    },
    editorProps: {
      attributes: {
        class: 'tiptap-content focus:outline-none min-h-[150px] p-6',
        style: 'font-family: var(--font-body, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif);',
      },
    },
  });

  // Sync external `value` changes back into the editor. Needed when the
  // parent replaces `content` programmatically (e.g. AI-generated HTML in
  // the Text module, or a "reset to default" button). We guard against
  // stomping on the user's active edits by only calling `setContent` when
  // the incoming string differs from what the editor already holds — this
  // prevents cursor jumps during normal typing where `onUpdate` bubbles up
  // then the parent echoes the same value back.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    // Both sides may serialise slightly differently — compare trimmed to
    // avoid meaningless whitespace-only re-renders. Also normalise away a
    // trailing empty paragraph on both sides: the trailing-node plugin
    // keeps re-adding one to the *live* document (so there's always a
    // cursor spot after an atom like a CTA button) even though the saved
    // `value` deliberately has it stripped — without this, every save
    // would look like a "real" diff and trigger a disruptive setContent
    // that resets the selection mid-edit.
    if (stripTrailingEmptyParagraphs(current.trim()) === stripTrailingEmptyParagraphs(next.trim())) return;
    editor.commands.setContent(next, false);
  }, [value, editor]);

  // Toggle HTML mode
  const toggleHtmlMode = () => {
    if (!showHtmlMode) {
      setHtmlContent(editor.getHTML());
      setShowHtmlMode(true);
    } else {
      const cleaned = stripTrailingEmptyParagraphs(htmlContent);
      editor.commands.setContent(cleaned);
      onChange(cleaned);
      setShowHtmlMode(false);
    }
  };

  // Handle image click for resize
  // Set true for ~400ms after any attribute commit so the ResizeObserver
  // doesn't mistake the DOM swap it causes for a real user drag.
  const suppressResizeCommitRef = useRef(false);
  const selectedImagePosRef = useRef(null);
  const handleImageClick = useCallback((e) => {
    if (e.target.tagName === 'IMG') {
      const img = e.target;
      // Use the raw attribute (not the DOM-resolved absolute `img.src`) so this
      // matches exactly what's stored on the ProseMirror node's `src` attr —
      // uploaded images are stored as relative URLs (e.g. /api/uploads/x.jpg),
      // and `img.src` always resolves those to an absolute URL, which never
      // matched in applyImageResize's doc.descendants lookup below.
      setSelectedImageSrc(img.getAttribute('src'));
      // Resolve and remember THIS exact image node's position (not just its
      // src) — if the same icon/image appears more than once in the doc,
      // matching by src alone would silently edit the wrong copy.
      try {
        const resolved = editor.view.posAtDOM(img, 0);
        selectedImagePosRef.current = resolved >= 0 ? resolved : null;
      } catch {
        selectedImagePosRef.current = null;
      }
      // A previous Height edit stores width in px, not %. Re-derive the %
      // (of the editor's content width) so clearing Height later doesn't
      // write the raw px number back out as a bogus "175%".
      // Layout attrs (float/justify/valign/radius/shadow) live on the
      // gradient wrapper <span> when a gradient overlay is active,
      // otherwise directly on the <img> — read from whichever has them.
      const wrapperEl = img.parentElement?.classList?.contains('editor-image-wrap') ? img.parentElement : null;
      const styleSource = wrapperEl || img;
      const currentWidth = (wrapperEl ? wrapperEl.style.width : null) || img.style.width || img.getAttribute('width') || '100%';
      let widthPct;
      if (currentWidth.endsWith('px')) {
        const containerWidth = editor?.view?.dom?.getBoundingClientRect().width || 0;
        widthPct = containerWidth > 0 ? Math.round((parseFloat(currentWidth) / containerWidth) * 100) : 100;
      } else {
        widthPct = parseFloat(currentWidth) || 100;
      }
      setImageWidth(String(Math.max(10, Math.min(100, widthPct))));

      // Current explicit height (only set when an admin previously typed
      // one in — otherwise it's 'auto' and this stays blank, meaning
      // "derive height from width" as before).
      const currentHeightStyle = (wrapperEl ? wrapperEl.style.height : null) || img.style.height || 'auto';
      const pxMatch = /^(\d+(?:\.\d+)?)px$/.exec(currentHeightStyle);
      setImageHeightPx(pxMatch ? pxMatch[1] : '');

      // Natural (intrinsic) aspect ratio, used to compute a proportional
      // width whenever the admin types a Height instead of dragging Width.
      const naturalW = img.naturalWidth || img.offsetWidth || 1;
      const naturalH = img.naturalHeight || img.offsetHeight || 1;
      imageNaturalRatioRef.current = naturalH > 0 ? naturalW / naturalH : 1;

      // Extract opacity
      const currentOpacity = img.style.opacity || '1';
      setImageOpacity(Math.round(parseFloat(currentOpacity) * 100).toString());
      setImageFloat(styleSource.style.float === 'left' || styleSource.style.float === 'right' ? styleSource.style.float : 'none');
      setImageJustify(styleSource.getAttribute('data-justify') || 'none');
      setImageValign(styleSource.getAttribute('data-valign') || 'none');
      const radiusMatch = /^(\d+(?:\.\d+)?)px$/.exec(styleSource.style.borderRadius || '');
      setImageRadius(radiusMatch ? radiusMatch[1] : '0');
      setImageShadow(styleSource.getAttribute('data-shadow') || 'none');
      const blurMatch = /blur\((\d+(?:\.\d+)?)px\)/.exec(img.style.filter || '');
      setImageBlur(blurMatch ? blurMatch[1] : '0');
      const brightnessMatch = /brightness\(([\d.]+)\)/.exec(img.style.filter || '');
      setImageBrightness(brightnessMatch ? Math.round(parseFloat(brightnessMatch[1]) * 100).toString() : '100');
      setImageGradient((wrapperEl && wrapperEl.getAttribute('data-gradient')) || 'none');
      setImageGradientColor((wrapperEl && wrapperEl.getAttribute('data-gradient-color')) || '#000000');
      setImageGradientOpacity((wrapperEl && wrapperEl.getAttribute('data-gradient-opacity')) || '60');
      setShowImageResize(true);

      // Let the browser's native corner-drag handle (CSS `resize`, see
      // .tiptap-editor-wrapper .ProseMirror img below) resize this element —
      // watch it so the final pixel size gets committed back into the
      // ProseMirror doc as a %, otherwise the next re-render would snap it
      // back to the last saved width.
      resizeObserverRef.current?.observe(img);
    }
  }, [editor]);

  // Commit a drag-resized DOM element's live pixel width back into the
  // ProseMirror doc as a percentage of the editor's content width.
  const commitDraggedWidth = useCallback((el, nodeType, matchAttr) => {
    if (!editor || !el.isConnected) return;
    const containerWidth = editor.view.dom.getBoundingClientRect().width;
    const elWidth = el.getBoundingClientRect().width;
    if (!containerWidth || !elWidth) return;
    const pct = Math.max(10, Math.min(100, Math.round((elWidth / containerWidth) * 100)));
    // Resolve the position from the actual dragged DOM element first —
    // robust even when the same image/embed appears more than once.
    // posAtDOM returns -1 (not an exception) when the node is detached, so
    // a strict `pos !== null` check alone isn't enough — must reject <0 too.
    let pos = null;
    try {
      const resolved = editor.view.posAtDOM(el, 0);
      if (resolved >= 0) pos = resolved;
    } catch {
      pos = null;
    }
    const nodeAtPos = pos !== null ? editor.state.doc.nodeAt(pos) : null;
    if (!nodeAtPos || nodeAtPos.type.name !== nodeType) pos = null;
    if (pos === null) {
      // Fallback: match by attribute, keeping only the first match.
      editor.state.doc.descendants((node, p) => {
        if (pos !== null) return false;
        if (node.type.name === nodeType && node.attrs[matchAttr.key] === matchAttr.value) {
          pos = p;
        }
      });
    }
    if (pos !== null) {
      // The attribute update replaces this DOM node, which would
      // otherwise retrigger the ResizeObserver on the now-detached element.
      suppressResizeCommitRef.current = true;
      setTimeout(() => { suppressResizeCommitRef.current = false; }, 400);
      const attrs = nodeType === 'image' ? { width: `${pct}%` } : { width: String(pct) };
      editor.chain().setNodeSelection(pos).updateAttributes(nodeType, attrs).run();
      if (nodeType === 'image') setImageWidth(String(pct));
      else if (nodeType === 'videoEmbed') setVideoWidth(String(pct));
      else if (nodeType === 'htmlEmbed') setHtmlEmbedWidth(String(pct));
    }
  }, [editor]);

  const resizeObserverRef = useRef(null);
  useEffect(() => {
    // A plain object stringifies every DOM element key to the same
    // "[object HTMLImageElement]", so debounce timers for different
    // images/videos/embeds were colliding and cancelling each other.
    const debounced = new Map();
    // ResizeObserver always delivers one "baseline" callback the instant
    // .observe() starts, per spec, even though nothing was actually
    // resized yet — without this guard that spurious first report was
    // being committed as if it were a real drag, snapping images/videos
    // to the wrong size the moment they were clicked (before the resize
    // dialog's own Apply button ever ran).
    const seenOnce = new WeakSet();
    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const el = entry.target;
        if (!seenOnce.has(el)) {
          seenOnce.add(el);
          continue;
        }
        if (suppressResizeCommitRef.current) continue;
        clearTimeout(debounced.get(el));
        debounced.set(el, setTimeout(() => {
          debounced.delete(el);
          if (!el.isConnected) return;
          if (el.tagName === 'IMG') {
            commitDraggedWidth(el, 'image', { key: 'src', value: el.getAttribute('src') });
          } else if (el.classList?.contains('video-embed')) {
            commitDraggedWidth(el, 'videoEmbed', { key: 'src', value: el.getAttribute('data-video-src') });
          } else if (el.classList?.contains('html-embed')) {
            commitDraggedWidth(el, 'htmlEmbed', { key: 'embedId', value: el.getAttribute('data-embed-id') });
          }
        }, 250));
      }
    });
    return () => resizeObserverRef.current?.disconnect();
  }, [commitDraggedWidth]);

  // Apply image resize, opacity and float — live (fires on every control
  // change, not just a final "Apply" click). Deliberately skips .focus()
  // so it doesn't steal focus away from the dialog's inputs while typing.
  const commitImageAttrs = useCallback(() => {
    if (!editor) return;
    // A typed Height takes over sizing: width becomes a pixel value
    // computed from the image's own natural aspect ratio so it never
    // stretches or crops. Leaving Height blank keeps the normal
    // width-percentage + auto-height behaviour.
    const heightNum = parseFloat(imageHeightPx);
    const useHeight = imageHeightPx !== '' && heightNum > 0;
    const widthNum = parseFloat(imageWidth);
    // Skip committing on a transient invalid/empty Width (e.g. mid-typing)
    // rather than writing a bogus "NaN%"/"%" into the doc.
    if (!useHeight && (imageWidth === '' || isNaN(widthNum))) return;

    // Prefer the exact node position captured at click time — robust even
    // when the same image (e.g. a reused icon) appears more than once.
    let imagePos = selectedImagePosRef.current;
    const nodeAtPos = imagePos !== null && imagePos !== undefined ? editor.state.doc.nodeAt(imagePos) : null;
    if (!nodeAtPos || nodeAtPos.type.name !== 'image') imagePos = null;
    if (imagePos === null && selectedImageSrc) {
      // Fallback: match by src, keeping only the first match.
      editor.state.doc.descendants((node, pos) => {
        if (imagePos !== null) return false;
        if (node.type.name === 'image' && node.attrs.src === selectedImageSrc) {
          imagePos = pos;
        }
      });
    }
    if (imagePos === null || imagePos === undefined) return;

    // The width change below resizes the DOM node, which the
    // ResizeObserver would otherwise see as a "drag" and try to
    // recompute/overwrite the % we just explicitly set here.
    suppressResizeCommitRef.current = true;
    setTimeout(() => { suppressResizeCommitRef.current = false; }, 400);
    const sizeAttrs = useHeight
      ? { width: `${Math.round(heightNum * imageNaturalRatioRef.current)}px`, height: `${heightNum}px` }
      : { width: `${Math.max(10, Math.min(100, widthNum))}%`, height: 'auto' };
    editor
      .chain()
      .setNodeSelection(imagePos)
      .updateAttributes('image', {
        ...sizeAttrs,
        opacity: imageOpacity,
        float: imageFloat,
        justify: imageJustify,
        valign: imageValign,
        borderRadius: imageRadius,
        boxShadow: imageShadow,
        blur: imageBlur,
        brightness: imageBrightness,
        gradient: imageGradient,
        gradientColor: imageGradientColor,
        gradientOpacity: imageGradientOpacity,
      })
      .run();
  }, [editor, selectedImageSrc, imageHeightPx, imageWidth, imageOpacity, imageFloat, imageJustify, imageValign, imageRadius, imageShadow, imageBlur, imageBrightness, imageGradient, imageGradientColor, imageGradientOpacity]);

  useEffect(() => {
    if (showImageResize) commitImageAttrs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageWidth, imageHeightPx, imageOpacity, imageFloat, imageJustify, imageValign, imageRadius, imageShadow, imageBlur, imageBrightness, imageGradient, imageGradientColor, imageGradientOpacity]);

  const closeImageResizeDialog = () => {
    editor?.chain().focus().run();
    setShowImageResize(false);
  };

  // Replace the selected image's src after a crop/rotate/flip edit,
  // resolving the node the same way commitImageAttrs does (exact position
  // first, src-match fallback) so a reused image elsewhere in the doc
  // isn't edited by mistake.
  const handleImageCropSaved = (newUrl) => {
    if (!editor) return;
    let imagePos = selectedImagePosRef.current;
    const nodeAtPos = imagePos !== null && imagePos !== undefined ? editor.state.doc.nodeAt(imagePos) : null;
    if (!nodeAtPos || nodeAtPos.type.name !== 'image') imagePos = null;
    if (imagePos === null && selectedImageSrc) {
      editor.state.doc.descendants((node, pos) => {
        if (imagePos !== null) return false;
        if (node.type.name === 'image' && node.attrs.src === selectedImageSrc) {
          imagePos = pos;
        }
      });
    }
    if (imagePos !== null && imagePos !== undefined) {
      editor.chain().setNodeSelection(imagePos).updateAttributes('image', { src: newUrl }).run();
      setSelectedImageSrc(newUrl);
    }
    setShowImageCrop(false);
  };

  // Handle video click for resize / float / display
  const selectedVideoPosRef = useRef(null);
  const handleVideoClick = useCallback((e) => {
    const wrapper = e.target.closest && e.target.closest('.video-embed');
    if (wrapper) {
      const src = wrapper.getAttribute('data-video-src') || wrapper.querySelector('iframe,video')?.getAttribute('src') || '';
      setSelectedVideoSrc(src);
      setVideoWidth((wrapper.style.width || '100%').replace('%', '').trim() || '100');
      setVideoFloat(/video-float-(\w+)/.exec(wrapper.className || '')?.[1] || 'none');
      setVideoDisplay(wrapper.classList.contains('video-embed-modal') ? 'modal' : 'inline');
      setShowVideoResize(true);
      try {
        const resolved = editor?.view.posAtDOM(wrapper, 0);
        selectedVideoPosRef.current = resolved >= 0 ? resolved : null;
      } catch {
        selectedVideoPosRef.current = null;
      }
      // Same native drag-resize commit as images (see handleImageClick).
      resizeObserverRef.current?.observe(wrapper);
    }
  }, [editor]);

  // Apply video resize, float and display mode
  const applyVideoResize = () => {
    if (editor && selectedVideoSrc) {
      let videoPos = selectedVideoPosRef.current;
      const nodeAtPos = videoPos !== null && videoPos !== undefined ? editor.state.doc.nodeAt(videoPos) : null;
      if (!nodeAtPos || nodeAtPos.type.name !== 'videoEmbed') videoPos = null;
      if (videoPos === null) {
        // Fallback: match by src, keeping only the first match.
        editor.state.doc.descendants((node, pos) => {
          if (videoPos !== null) return false;
          if (node.type.name === 'videoEmbed' && node.attrs.src === selectedVideoSrc) {
            videoPos = pos;
          }
        });
      }

      if (videoPos !== null) {
        suppressResizeCommitRef.current = true;
        setTimeout(() => { suppressResizeCommitRef.current = false; }, 400);
        editor
          .chain()
          .focus()
          .setNodeSelection(videoPos)
          .updateAttributes('videoEmbed', {
            width: videoWidth,
            float: videoFloat,
            display: videoDisplay,
          })
          .run();
      }
    }
    setShowVideoResize(false);
  };

  // Handle clicking an embedded HTML block for resize/position
  const selectedHtmlEmbedPosRef = useRef(null);
  const handleHtmlEmbedClick = useCallback((e) => {
    const wrapper = e.target.closest && e.target.closest('.html-embed');
    if (wrapper) {
      setSelectedHtmlEmbedId(wrapper.getAttribute('data-embed-id') || '');
      setHtmlEmbedWidth((wrapper.style.width || '100%').replace('%', '').trim() || '100');
      setHtmlEmbedFloat(/html-embed-float-(\w+)/.exec(wrapper.className || '')?.[1] || 'none');
      setShowHtmlEmbedResize(true);
      try {
        const resolved = editor?.view.posAtDOM(wrapper, 0);
        selectedHtmlEmbedPosRef.current = resolved >= 0 ? resolved : null;
      } catch {
        selectedHtmlEmbedPosRef.current = null;
      }
      resizeObserverRef.current?.observe(wrapper);
    }
  }, [editor]);

  // Handle clicking an existing CTA button for editing
  const handleCtaClick = useCallback((e) => {
    const wrapper = e.target.closest && e.target.closest('.slh-cta-button');
    if (wrapper) {
      e.preventDefault();
      const anchor = wrapper.querySelector('a');
      setCtaText(anchor?.textContent?.trim() || 'Learn More');
      setCtaUrl(anchor?.getAttribute('href') || '');
      setCtaTarget(wrapper.getAttribute('data-cta-target') || 'same');
      try {
        const resolved = editor?.view.posAtDOM(wrapper, 0);
        selectedCtaPosRef.current = resolved >= 0 ? resolved : null;
      } catch {
        selectedCtaPosRef.current = null;
      }
      setShowCtaDialog(true);
    }
  }, [editor]);

  // Apply HTML embed resize + float
  const applyHtmlEmbedResize = () => {
    if (editor && selectedHtmlEmbedId) {
      let pos = selectedHtmlEmbedPosRef.current;
      const nodeAtPos = pos !== null && pos !== undefined ? editor.state.doc.nodeAt(pos) : null;
      if (!nodeAtPos || nodeAtPos.type.name !== 'htmlEmbed') pos = null;
      if (pos === null) {
        // Fallback: match by embedId, keeping only the first match.
        editor.state.doc.descendants((node, p) => {
          if (pos !== null) return false;
          if (node.type.name === 'htmlEmbed' && node.attrs.embedId === selectedHtmlEmbedId) {
            pos = p;
          }
        });
      }

      if (pos !== null) {
        suppressResizeCommitRef.current = true;
        setTimeout(() => { suppressResizeCommitRef.current = false; }, 400);
        editor
          .chain()
          .focus()
          .setNodeSelection(pos)
          .updateAttributes('htmlEmbed', {
            width: htmlEmbedWidth,
            float: htmlEmbedFloat,
          })
          .run();
      }
    }
    setShowHtmlEmbedResize(false);
  };

  // Open the Table Properties dialog, seeded from the table under the cursor
  const openTableProperties = () => {
    if (!editor || !editor.isActive('table')) {
      alert('Click inside a table first, then open Table Properties.');
      return;
    }
    const attrs = editor.getAttributes('table');
    setTableWidthMode(attrs.widthMode || 'percent');
    setTableWidthValue((attrs.widthValue || '100').toString());
    setTableAlign(attrs.align || 'left');
    setTableBgColor(attrs.bgColor || '');
    setTableBorderColor(attrs.borderColor || '#d1d5db');
    setTableBorderWidth((attrs.borderWidth || '1').toString());
    setTableCellPadding((attrs.cellPadding || '').toString());
    setTableCellHeight((attrs.cellHeight || '').toString());
    setShowTablePropsDialog(true);
  };

  // Apply width/justification/background/border back to that same table
  const applyTableProperties = () => {
    editor.chain().focus().updateAttributes('table', {
      widthMode: tableWidthMode,
      widthValue: tableWidthValue,
      align: tableAlign,
      bgColor: tableBgColor || null,
      borderColor: tableBorderColor,
      borderWidth: tableBorderWidth,
      cellPadding: tableCellPadding,
      cellHeight: tableCellHeight,
    }).run();
    setShowTablePropsDialog(false);
  };

  // Open the Cell Alignment dialog, seeded from the cell under the cursor
  // (or the first cell of a dragged multi-cell selection).
  const openCellAlignment = () => {
    if (!editor || !editor.isActive('table')) {
      alert('Click inside a table cell first, then open Cell Alignment.');
      return;
    }
    const attrs = editor.isActive('tableHeader') ? editor.getAttributes('tableHeader') : editor.getAttributes('tableCell');
    setCellAlign(attrs.align || 'left');
    setCellValign(attrs.valign || 'top');
    setShowCellAlignDialog(true);
  };

  const applyCellAlignmentFromDialog = () => {
    applyCellAlignment(editor, { align: cellAlign, valign: cellValign });
    setShowCellAlignDialog(false);
  };

  // Open the "Embed HTML" insert dialog
  const addHtmlEmbed = () => {
    setHtmlEmbedCode('');
    setShowHtmlEmbedDialog(true);
  };

  // Insert the pasted HTML as a new resizable/floatable block
  const insertHtmlEmbed = () => {
    if (htmlEmbedCode.trim()) {
      // Insert embed + trailing paragraph in ONE call: after inserting an atom
      // the selection becomes a NodeSelection on it, so a second chained
      // insertContent would replace (delete) the just-inserted embed.
      // insertAfterSelection also guards against replacing an
      // already-selected image/video/embed node.
      insertAfterSelection(editor, [
        { type: 'htmlEmbed', attrs: { html: htmlEmbedCode, width: '100', float: 'none', embedId: genEmbedId() } },
        { type: 'paragraph' },
      ]);
    }
    setShowHtmlEmbedDialog(false);
    setHtmlEmbedCode('');
  };

  // Open the "Insert ready-made embed" dialog for the chosen template kind.
  const openEmbedTemplate = (kind) => {
    setEmbedTemplateKind(kind);
    setEmbedTemplateValue('');
    setShowEmbedTemplateDialog(true);
  };

  // Build the iframe snippet for the chosen template and insert it as the
  // same resizable/floatable htmlEmbed block "Embed HTML" produces.
  const insertEmbedTemplate = () => {
    const html = buildEmbedTemplateHtml(embedTemplateKind, embedTemplateValue);
    if (!html) return;
    insertAfterSelection(editor, [
      { type: 'htmlEmbed', attrs: { html, width: '100', float: 'none', embedId: genEmbedId() } },
      { type: 'paragraph' },
    ]);
    setShowEmbedTemplateDialog(false);
    setEmbedTemplateValue('');
  };

  // Read an uploaded .html/.htm/.txt file and drop its text content into
  // the Embed HTML textarea so the admin can review/tweak before inserting.
  const handleHtmlFileUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setHtmlEmbedCode(String(reader.result || ''));
    reader.onerror = () => toast.error("Couldn't read that file");
    reader.readAsText(file);
  };

  // Handle image or video library selection
  const handleImageLibrarySelect = (url, opts = {}) => {
    if (opts.isVideo) {
      const html = `<div class="video-embed" data-video-src="${url}" data-video-kind="file" style="width: 100%;"><div class="video-embed-inner"><video src="${url}" controls playsinline preload="metadata"></video></div></div><p></p>`;
      editor.chain().focus().insertContent(html).run();
    } else {
      editor.chain().focus().setImage({
        src: url,
        width: '100%',
        opacity: '100'
      }).run();
    }
  };

  // Handle image upload
  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/upload?skip_watermark=true`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.url) {
        editor.chain().focus().setImage({ 
          src: response.data.url,
          width: '100%',
          opacity: '100'
        }).run();
      }
    } catch (error) {
      console.error('Image upload failed:', error);
    }
  };

  // Handle link
  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    setLinkUrl(previousUrl || '');
    setShowLinkDialog(true);
  };

  const setLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setShowLinkDialog(false);
    setLinkUrl('');
  };

  // Open the "Insert CTA Button" dialog in insert mode (no existing node)
  const addCtaButton = () => {
    selectedCtaPosRef.current = null;
    setCtaText('Learn More');
    setCtaUrl('');
    setCtaTarget('same');
    setShowCtaDialog(true);
  };

  // Insert a new CTA button, or update the one the admin clicked into edit
  const applyCtaButton = () => {
    if (!ctaText.trim() || !ctaUrl.trim()) {
      toast.error('Button copy and URL are both required');
      return;
    }
    if (!isSafeCtaUrl(ctaUrl.trim())) {
      toast.error('Enter a valid http(s), mailto, tel, or relative URL');
      return;
    }
    const attrs = { text: ctaText.trim(), url: ctaUrl.trim(), target: ctaTarget };
    if (selectedCtaPosRef.current !== null) {
      const nodeAtPos = editor.state.doc.nodeAt(selectedCtaPosRef.current);
      if (nodeAtPos && nodeAtPos.type.name === 'ctaButton') {
        editor.chain().focus().setNodeSelection(selectedCtaPosRef.current).updateAttributes('ctaButton', attrs).run();
      }
    } else {
      insertAfterSelection(editor, [
        { type: 'ctaButton', attrs },
        { type: 'paragraph' },
      ]);
    }
    setShowCtaDialog(false);
  };

  // Handle YouTube
  const addYouTube = () => {
    setVideoUrl('');
    setShowVideoDialog(true);
  };

  const embedVideo = () => {
    if (videoUrl) {
      const src = getVideoEmbedSrc(videoUrl);
      const html = `<div class="video-embed" style="width: 100%;"><div class="video-embed-inner"><iframe src="${src}" frameborder="0" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe></div></div><p></p>`;
      editor.chain().focus().insertContent(html).run();
    }
    setShowVideoDialog(false);
    setVideoUrl('');
  };

  if (!editor) {
    return null;
  }

  return (
    <div className="tiptap-editor-wrapper border rounded-lg">
      {/* Toolbar */}
      {!showHtmlMode && (
        <div className="border-b bg-gray-50 p-2 flex flex-wrap gap-1">
          {/* History */}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="h-8 w-8 p-0"
          >
            <Undo className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="h-8 w-8 p-0"
          >
            <Redo className="h-4 w-4" />
          </Button>

          <div className="w-px h-8 bg-gray-300 mx-1" />

          {/* Headings */}
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('paragraph') ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().setParagraph().run()}
            className="h-8 w-8 p-0"
            title="Normal text"
            data-testid="tiptap-normal-text"
          >
            <Pilcrow className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className="h-8 w-8 p-0"
          >
            <Heading1 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className="h-8 w-8 p-0"
          >
            <Heading2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className="h-8 w-8 p-0"
          >
            <Heading3 className="h-4 w-4" />
          </Button>

          <div className="w-px h-8 bg-gray-300 mx-1" />

          {/* Text Formatting */}
          <FontFamilyPopover editor={editor} />
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('bold') ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleBold().run()}
            className="h-8 w-8 p-0"
            title="Bold"
            data-testid="tiptap-bold-btn"
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('italic') ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className="h-8 w-8 p-0"
            title="Italic"
            data-testid="tiptap-italic-btn"
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('underline') ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className="h-8 w-8 p-0"
            title="Underline"
            data-testid="tiptap-underline-btn"
          >
            <UnderlineIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('strike') ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className="h-8 w-8 p-0"
          >
            <Strikethrough className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('code') ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleCode().run()}
            className="h-8 w-8 p-0"
          >
            <Code className="h-4 w-4" />
          </Button>
          <PaletteColorPopover
            icon={Baseline}
            title="Text Color"
            testIdPrefix="tiptap-text-color"
            activeColor={editor.getAttributes('textStyle').color}
            onPick={(hex) => editor.chain().focus().setColor(hex).run()}
            onClear={() => editor.chain().focus().unsetColor().run()}
          />
          <PaletteColorPopover
            icon={Highlighter}
            title="Background Color"
            testIdPrefix="tiptap-bg-color"
            activeColor={editor.getAttributes('highlight').color}
            onPick={(hex) => editor.chain().focus().setHighlight({ color: hex }).run()}
            onClear={() => editor.chain().focus().unsetHighlight().run()}
          />

          <div className="w-px h-8 bg-gray-300 mx-1" />

          {/* Lists */}
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className="h-8 w-8 p-0"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className="h-8 w-8 p-0"
          >
            <ListOrdered className="h-4 w-4" />
          </Button>

          <div className="w-px h-8 bg-gray-300 mx-1" />

          {/* Alignment */}
          <Button
            type="button"
            size="sm"
            variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className="h-8 w-8 p-0"
          >
            <AlignLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className="h-8 w-8 p-0"
          >
            <AlignCenter className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className="h-8 w-8 p-0"
          >
            <AlignRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={editor.isActive({ textAlign: 'justify' }) ? 'default' : 'ghost'}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className="h-8 w-8 p-0"
          >
            <AlignJustify className="h-4 w-4" />
          </Button>

          <div className="w-px h-8 bg-gray-300 mx-1" />

          {/* Media */}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setShowImageLibrary(true)}
            className="h-8 px-3"
            data-testid="tiptap-image-library-btn"
          >
            <ImageIcon className="h-4 w-4 mr-1" />
            Library
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              if (selectedImageSrc) {
                // If we have a recently clicked image, open resize dialog
                setShowImageResize(true);
              } else {
                // Try to find any image near cursor or selected
                const { state } = editor;
                const { selection } = state;
                const node = selection.node;
                
                if (node && node.type.name === 'image') {
                  setSelectedImageSrc(node.attrs.src);
                  setImageWidth((node.attrs.width || '100%').replace('%', '').replace('px', ''));
                  setImageOpacity(node.attrs.opacity || '100');
                  setShowImageResize(true);
                } else {
                  // Search for nearest image
                  let foundImage = false;
                  state.doc.descendants((n, pos) => {
                    if (n.type.name === 'image' && !foundImage) {
                      setSelectedImageSrc(n.attrs.src);
                      setImageWidth((n.attrs.width || '100%').replace('%', '').replace('px', ''));
                      setImageOpacity(n.attrs.opacity || '100');
                      setShowImageResize(true);
                      foundImage = true;
                      return false;
                    }
                  });
                  
                  if (!foundImage) {
                    alert('Please click on an image in the editor first, then click this button to resize it.');
                  }
                }
              }
            }}
            disabled={!editor}
            className="h-8 w-8 p-0"
            title="Resize image (click an image first)"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={addLink}
            className="h-8 w-8 p-0"
            title="Add link"
          >
            <LinkIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={addYouTube}
            className="h-8 w-8 p-0"
            title="Embed video (YouTube, Vimeo, etc.)"
            data-testid="tiptap-embed-video-btn"
          >
            <Video className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={addCtaButton}
            className="h-8 px-2 gap-1"
            title="Insert CTA button"
            data-testid="tiptap-cta-insert-btn"
          >
            <MousePointerClick className="h-4 w-4" />
            <span className="text-xs">CTA</span>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              if (!editor) return;
              const { state } = editor;
              const { selection } = state;
              const node = selection.node;

              if (node && node.type.name === 'videoEmbed') {
                selectedVideoPosRef.current = selection.from;
                setSelectedVideoSrc(node.attrs.src);
                setVideoWidth((node.attrs.width || '100').toString());
                setVideoFloat(node.attrs.float || 'none');
                setVideoDisplay(node.attrs.display || 'inline');
                setShowVideoResize(true);
              } else {
                let foundVideo = false;
                state.doc.descendants((n, pos) => {
                  if (n.type.name === 'videoEmbed' && !foundVideo) {
                    selectedVideoPosRef.current = pos;
                    setSelectedVideoSrc(n.attrs.src);
                    setVideoWidth((n.attrs.width || '100').toString());
                    setVideoFloat(n.attrs.float || 'none');
                    setVideoDisplay(n.attrs.display || 'inline');
                    setShowVideoResize(true);
                    foundVideo = true;
                    return false;
                  }
                });
                if (!foundVideo) {
                  alert('Please click on a video in the editor first, then click this button to resize/float it.');
                }
              }
            }}
            disabled={!editor}
            className="h-8 w-8 p-0"
            title="Video settings — resize, float, popup (click a video first)"
            data-testid="tiptap-resize-video-btn"
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={addHtmlEmbed}
            className="h-8 px-3"
            title="Embed custom HTML — a map, widget, iframe or any markup"
            data-testid="tiptap-embed-html-btn"
          >
            <Code2 className="h-4 w-4 mr-1" />
            Embed HTML
          </Button>
          <EmbedTemplatesPopover onPick={openEmbedTemplate} />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              if (!editor) return;
              const { state } = editor;
              const { selection } = state;
              const node = selection.node;

              if (node && node.type.name === 'htmlEmbed') {
                selectedHtmlEmbedPosRef.current = selection.from;
                setSelectedHtmlEmbedId(node.attrs.embedId || '');
                setHtmlEmbedWidth((node.attrs.width || '100').toString());
                setHtmlEmbedFloat(node.attrs.float || 'none');
                setShowHtmlEmbedResize(true);
              } else {
                let foundEmbed = false;
                state.doc.descendants((n, pos) => {
                  if (n.type.name === 'htmlEmbed' && !foundEmbed) {
                    selectedHtmlEmbedPosRef.current = pos;
                    setSelectedHtmlEmbedId(n.attrs.embedId || '');
                    setHtmlEmbedWidth((n.attrs.width || '100').toString());
                    setHtmlEmbedFloat(n.attrs.float || 'none');
                    setShowHtmlEmbedResize(true);
                    foundEmbed = true;
                    return false;
                  }
                });
                if (!foundEmbed) {
                  alert('Please click on an embedded HTML block in the editor first, then click this button to resize/position it.');
                }
              }
            }}
            disabled={!editor}
            className="h-8 w-8 p-0"
            title="HTML embed settings — resize, position (click an embed first)"
            data-testid="tiptap-resize-html-embed-btn"
          >
            <Settings2 className="h-4 w-4" />
          </Button>
          <TableToolbarPopover editor={editor} onOpenProperties={openTableProperties} onOpenCellAlign={openCellAlignment} />
          <ShapesToolbarButton editor={editor} />
        </div>
      )}

      {/* HTML/Visual Toggle */}
      <div className="border-b bg-gray-100 p-2">
        <Button
          type="button"
          size="sm"
          variant={showHtmlMode ? 'default' : 'outline'}
          onClick={toggleHtmlMode}
          className="text-xs"
        >
          <FileCode className="h-4 w-4 mr-1" />
          {showHtmlMode ? 'Back to Visual' : 'Edit HTML'}
        </Button>
      </div>

      {/* Editor Content */}
      {!showHtmlMode ? (
        <div className="bg-white" onClick={(e) => { handleImageClick(e); handleVideoClick(e); handleHtmlEmbedClick(e); handleCtaClick(e); }}>
          <EditorContent editor={editor} />
          <style>{`
            .tiptap-editor-wrapper .ProseMirror {
              min-height: 150px;
              padding: 1.5rem;
              font-family: var(--font-body, inherit);
              font-size: var(--base-font-size, 16px);
              font-weight: var(--body-weight, 400);
              line-height: var(--body-line-height, 1.6);
              color: #333;
            }
            .tiptap-editor-wrapper .ProseMirror:focus {
              outline: none;
            }
            
            /* Typography — pulls live from Site Styles' CSS variables
               (set globally on :root, admin dashboard included) so what you
               see while editing matches the live site's fonts/sizes/weights.
               Falls back to these defaults only when a var isn't set. */
            .tiptap-editor-wrapper .ProseMirror h1 {
              font-family: var(--font-heading, inherit);
              font-size: var(--h1-size, 2.5rem);
              font-weight: var(--heading-weight, 700);
              line-height: var(--heading-line-height, 1.2);
              margin-top: 1.5rem;
              margin-bottom: 1rem;
              color: #111;
            }
            .tiptap-editor-wrapper .ProseMirror h2 {
              font-family: var(--font-heading, inherit);
              font-size: var(--h2-size, 2rem);
              font-weight: var(--heading-weight, 600);
              line-height: var(--heading-line-height, 1.3);
              margin-top: 1.5rem;
              margin-bottom: 0.875rem;
              color: #111;
            }
            .tiptap-editor-wrapper .ProseMirror h3 {
              font-family: var(--font-heading, inherit);
              font-size: var(--h3-size, 1.5rem);
              font-weight: var(--heading-weight, 600);
              line-height: var(--heading-line-height, 1.4);
              margin-top: 1.25rem;
              margin-bottom: 0.75rem;
              color: #111;
            }
            .tiptap-editor-wrapper .ProseMirror p {
              margin-top: 0.75rem;
              margin-bottom: 0.75rem;
              font-size: var(--base-font-size, 16px);
              line-height: var(--body-line-height, 1.6);
            }
            .tiptap-editor-wrapper .ProseMirror strong {
              font-weight: 700;
              color: #111;
            }
            .tiptap-editor-wrapper .ProseMirror em {
              font-style: italic;
            }
            .tiptap-editor-wrapper .ProseMirror u {
              text-decoration: underline;
            }
            .tiptap-editor-wrapper .ProseMirror s {
              text-decoration: line-through;
            }
            .tiptap-editor-wrapper .ProseMirror code {
              background: #f3f4f6;
              padding: 0.2em 0.4em;
              border-radius: 3px;
              font-size: 0.9em;
              font-family: 'Courier New', monospace;
              color: #e83e8c;
            }
            /* Text Color applies a <span style="color:..."> wrapper; bold
               has its own explicit colour above, which (being set on the
               element itself, not inherited) otherwise always wins over an
               ancestor span's colour. Only override when actually nested
               inside such a span, so plain bold keeps its normal look
               everywhere else. (Inline code can't be coloured at all —
               StarterKit's Code mark excludes every other mark, so it
               never ends up inside a colour span to begin with.) */
            .tiptap-editor-wrapper .ProseMirror span[style*="color"] strong {
              color: inherit;
            }
            
            /* Lists — default to the List Font/Size from Site Styles,
               falling back to the Normal Font/size already set on the
               parent .ProseMirror. */
            .tiptap-editor-wrapper .ProseMirror ul,
            .tiptap-editor-wrapper .ProseMirror ol {
              padding-left: 2rem;
              margin: 1rem 0;
              font-family: var(--font-list, inherit);
              font-size: var(--list-font-size, inherit);
            }
            .tiptap-editor-wrapper .ProseMirror li {
              margin: 0.5rem 0;
            }
            /* Tiptap's list schema wraps each item's text in a <p>, and the
               ".ProseMirror p" rule above (font-size: var(--base-font-size))
               has higher specificity than the ul/ol rule, silently
               overriding List Font Size. Force it back to inherit from the
               ul/ol ancestor. */
            .tiptap-editor-wrapper .ProseMirror li p {
              font-size: inherit;
              font-family: inherit;
            }
            .tiptap-editor-wrapper .ProseMirror ul {
              list-style-type: disc;
            }
            .tiptap-editor-wrapper .ProseMirror ol {
              list-style-type: decimal;
            }
            
            /* Blockquote */
            .tiptap-editor-wrapper .ProseMirror blockquote {
              border-left: 4px solid #e5e7eb;
              padding-left: 1rem;
              margin: 1.5rem 0;
              color: #6b7280;
              font-style: italic;
            }
            
            /* Placeholder */
            .tiptap-editor-wrapper .ProseMirror p.is-editor-empty:first-child::before {
              color: #adb5bd;
              content: attr(data-placeholder);
              float: left;
              height: 0;
              pointer-events: none;
            }
            
            /* Images - WYSIWYG with hover effect for resize. Width-only:
               resize: horizontal keeps the drag handle from setting an
               explicit height, and height: auto (set on the node itself,
               see the Image extension above) keeps the image's natural
               aspect ratio so it's never stretched/cropped into a square. */
            .tiptap-editor-wrapper .ProseMirror img {
              max-width: 100%;
              height: auto;
              cursor: pointer;
              border-radius: 8px;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              margin: 1.5rem 0;
              display: block;
              transition: box-shadow 0.2s, transform 0.2s;
              resize: horizontal;
              overflow: hidden;
            }
            .tiptap-editor-wrapper .ProseMirror img:hover {
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              transform: scale(1.01);
            }
            .tiptap-editor-wrapper .ProseMirror img.ProseMirror-selectednode {
              outline: 3px solid #2563eb;
              outline-offset: 2px;
            }
            
            /* Links - WYSIWYG */
            .tiptap-editor-wrapper .ProseMirror a {
              color: #2563eb;
              text-decoration: underline;
              cursor: pointer;
            }
            .tiptap-editor-wrapper .ProseMirror a:hover {
              color: #1d4ed8;
            }
            
            /* Code blocks */
            .tiptap-editor-wrapper .ProseMirror pre {
              background: #1e1e1e;
              color: #d4d4d4;
              font-family: 'Courier New', monospace;
              padding: 1rem;
              border-radius: 8px;
              margin: 1.5rem 0;
              overflow-x: auto;
            }
            .tiptap-editor-wrapper .ProseMirror pre code {
              background: none;
              color: inherit;
              font-size: 0.875rem;
              padding: 0;
            }
            
            /* Video embeds - WYSIWYG - FIXED */
            .tiptap-editor-wrapper .ProseMirror .youtube-video,
            .tiptap-editor-wrapper .ProseMirror div[data-youtube-video] {
              margin: 2rem auto;
              max-width: 100%;
              position: relative;
            }

            /* Drag-resize handle on the video box — width-only so the 16:9
               aspect ratio (driven by .video-embed-inner's padding-bottom
               trick) stays correct; commits back to a % width on release
               via the ResizeObserver in handleVideoClick. */
            .tiptap-editor-wrapper .video-embed {
              resize: horizontal;
              overflow: hidden;
            }
            
            .tiptap-editor-wrapper .ProseMirror .youtube-video iframe,
            .tiptap-editor-wrapper .ProseMirror div[data-youtube-video] iframe {
              width: 100%;
              height: 360px;
              border: none;
              border-radius: 8px;
              box-shadow: 0 4px 6px rgba(0,0,0,0.1);
              display: block;
            }
            
            /* Generic iframe support */
            .tiptap-editor-wrapper .ProseMirror iframe {
              max-width: 100%;
              display: block;
              margin: 2rem 0;
              border-radius: 8px;
            }

            /* Raw HTML embeds — dashed box so an empty/stripped embed is
               still visible and clickable in the editor; drag-resize
               handle same as video (width-only, commits back to %). */
            .tiptap-editor-wrapper .html-embed {
              resize: horizontal;
              overflow: hidden;
              border: 1px dashed #c7c7c7;
              border-radius: 8px;
              min-height: 60px;
              margin: 1.5rem 0;
              cursor: pointer;
              max-width: 100%;
            }
            .tiptap-editor-wrapper .html-embed-inner {
              width: 100%;
            }
            .tiptap-editor-wrapper .ProseMirror .html-embed.ProseMirror-selectednode {
              outline: 3px solid #2563eb;
              outline-offset: 2px;
            }
            .tiptap-editor-wrapper .html-embed.html-embed-float-left {
              float: left;
              margin: 0.25rem 1.5rem 1rem 0;
            }
            .tiptap-editor-wrapper .html-embed.html-embed-float-right {
              float: right;
              margin: 0.25rem 0 1rem 1.5rem;
            }

            /* Tables — per-column drag-resize + cell selection affordances
               (editor-only; the actual width/align/border/bg styling is
               driven by CSS vars set per-table, shared with the public
               site via App.css's .slh-table rules). */
            .tiptap-editor-wrapper .ProseMirror .tableWrapper {
              overflow-x: auto;
              margin: 1rem 0;
            }
            .tiptap-editor-wrapper .ProseMirror table {
              border-collapse: collapse;
              table-layout: fixed;
            }
            .tiptap-editor-wrapper .ProseMirror table:not(.slh-table) {
              margin: 0;
            }
            .tiptap-editor-wrapper .ProseMirror table td,
            .tiptap-editor-wrapper .ProseMirror table th {
              position: relative;
              box-sizing: border-box;
              min-width: 1em;
            }
            .tiptap-editor-wrapper .ProseMirror table .selectedCell:after {
              background: rgba(37, 99, 235, 0.15);
              content: "";
              position: absolute;
              inset: 0;
              pointer-events: none;
              z-index: 2;
            }
            .tiptap-editor-wrapper .ProseMirror table .column-resize-handle {
              background-color: #2563eb;
              bottom: -2px;
              pointer-events: none;
              position: absolute;
              right: -2px;
              top: 0;
              width: 3px;
            }
            .tiptap-editor-wrapper .ProseMirror.resize-cursor {
              cursor: col-resize;
            }

            /* Alignment */
            .tiptap-editor-wrapper .ProseMirror [style*="text-align: center"] {
              text-align: center;
            }
            .tiptap-editor-wrapper .ProseMirror [style*="text-align: right"] {
              text-align: right;
            }
            .tiptap-editor-wrapper .ProseMirror [style*="text-align: justify"] {
              text-align: justify;
            }
          `}</style>
        </div>
      ) : (
        <textarea
          value={htmlContent}
          onChange={(e) => setHtmlContent(e.target.value)}
          className="w-full h-[500px] p-4 font-mono text-sm border-0 focus:outline-none focus:ring-0 resize-none"
          placeholder="Edit HTML here..."
        />
      )}

      {/* Image Library Modal */}
      <ImageLibraryModal
        open={showImageLibrary}
        onClose={() => setShowImageLibrary(false)}
        onSelect={handleImageLibrarySelect}
        allowEdit={false}
      />

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="sm:max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle>Add Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="link-url">URL</Label>
              <Input
                id="link-url"
                type="url"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className="rounded-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowLinkDialog(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={setLink}
              className="rounded-none"
            >
              Add Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Embed Dialog */}
      <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
        <DialogContent className="sm:max-w-md rounded-none">
          <DialogHeader>
            <DialogTitle>Embed Video</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="video-url">Video URL</Label>
              <Input
                id="video-url"
                type="url"
                placeholder="YouTube, Vimeo, or direct video URL"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                className="rounded-none"
              />
              <p className="text-xs text-muted-foreground">
                Supports YouTube, Vimeo, and other video platforms
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowVideoDialog(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={embedVideo}
              className="rounded-none"
            >
              Embed Video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CTA Button Dialog — insert or edit */}
      <Dialog open={showCtaDialog} onOpenChange={setShowCtaDialog}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="tiptap-cta-dialog">
          <DialogHeader>
            <DialogTitle>{selectedCtaPosRef.current !== null ? "Edit CTA Button" : "Insert CTA Button"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cta-text">Button Copy</Label>
              <Input
                id="cta-text"
                placeholder="e.g. Book a Viewing"
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                className="rounded-none"
                data-testid="tiptap-cta-text-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta-url">Destination URL</Label>
              <Input
                id="cta-url"
                type="url"
                placeholder="https://example.com"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                className="rounded-none"
                data-testid="tiptap-cta-url-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cta-target">Open In</Label>
              <select
                id="cta-target"
                value={ctaTarget}
                onChange={(e) => setCtaTarget(e.target.value)}
                className="w-full h-10 px-3 py-2 border border-input rounded-none bg-background text-sm"
                data-testid="tiptap-cta-target-select"
              >
                <option value="same">Same tab</option>
                <option value="new">New tab</option>
                <option value="modal">Modal popup</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCtaDialog(false)}
              className="rounded-none"
              data-testid="tiptap-cta-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={applyCtaButton}
              className="rounded-none"
              data-testid="tiptap-cta-save-btn"
            >
              {selectedCtaPosRef.current !== null ? "Update" : "Insert"} CTA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Settings Dialog */}
      <Dialog open={showVideoResize} onOpenChange={setShowVideoResize}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="tiptap-video-resize-dialog">
          <DialogHeader>
            <DialogTitle>Video Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="video-width">Width (%)</Label>
              <div className="flex gap-2">
                <Input
                  id="video-width"
                  type="number"
                  min="20"
                  max="100"
                  value={videoWidth}
                  onChange={(e) => setVideoWidth(e.target.value)}
                  className="rounded-none"
                  data-testid="tiptap-video-width-input"
                />
                {["25", "50", "75", "100"].map((w) => (
                  <Button
                    key={w}
                    type="button"
                    variant="outline"
                    onClick={() => setVideoWidth(w)}
                    className="rounded-none"
                    data-testid={`tiptap-video-width-${w}`}
                  >
                    {w}%
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Click a video in the editor to resize it (20-100%)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Text wrap</Label>
              <div className="flex gap-2">
                {[
                  { value: "none", label: "No wrap" },
                  { value: "left", label: "Float left" },
                  { value: "right", label: "Float right" },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={videoFloat === opt.value ? "default" : "outline"}
                    onClick={() => setVideoFloat(opt.value)}
                    className="rounded-none flex-1"
                    data-testid={`tiptap-video-float-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Float left/right so body text wraps beside the video — reduce the width first for the best result.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Playback</Label>
              <div className="flex gap-2">
                {[
                  { value: "inline", label: "Play inline" },
                  { value: "modal", label: "Open in popup" },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={videoDisplay === opt.value ? "default" : "outline"}
                    onClick={() => setVideoDisplay(opt.value)}
                    className="rounded-none flex-1"
                    data-testid={`tiptap-video-display-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                "Open in popup" shows a clickable thumbnail — visitors click it to play the video in a modal.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowVideoResize(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={applyVideoResize}
              className="rounded-none"
              data-testid="tiptap-video-resize-apply"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed HTML Dialog — paste a map/widget/iframe snippet */}
      <Dialog open={showHtmlEmbedDialog} onOpenChange={setShowHtmlEmbedDialog}>
        <DialogContent className="sm:max-w-lg rounded-none" data-testid="tiptap-html-embed-dialog">
          <DialogHeader>
            <DialogTitle>Embed HTML</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="html-embed-code">HTML code</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => htmlFileInputRef.current?.click()}
                  className="h-7 px-2 rounded-none gap-1.5"
                  data-testid="tiptap-html-embed-upload-btn"
                >
                  <UploadIcon className="h-3.5 w-3.5" />
                  Upload File
                </Button>
                <input
                  ref={htmlFileInputRef}
                  type="file"
                  accept=".html,.htm,.txt,text/html,text/plain"
                  className="hidden"
                  onChange={handleHtmlFileUpload}
                  data-testid="tiptap-html-embed-file-input"
                />
              </div>
              <textarea
                id="html-embed-code"
                value={htmlEmbedCode}
                onChange={(e) => setHtmlEmbedCode(e.target.value)}
                placeholder='Paste an embed snippet, e.g. <iframe src="https://maps.google.com/..."></iframe>'
                rows={8}
                className="w-full p-3 font-mono text-xs border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="tiptap-html-embed-textarea"
              />
              <p className="text-xs text-muted-foreground">
                Paste code directly or upload a .html/.txt file — either way it's inserted
                as a block you can resize and position just like an image or video.
                For security, &lt;script&gt;, &lt;style&gt;, form and button tags are
                stripped on the live site — use an &lt;iframe&gt; embed (maps, forms,
                widgets) if the provider offers one.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowHtmlEmbedDialog(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={insertHtmlEmbed}
              className="rounded-none"
              data-testid="tiptap-html-embed-insert"
            >
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ready-made embed templates — Google Map / Google Calendar. One
          input away from a working iframe so admins don't hand-write HTML. */}
      <Dialog open={showEmbedTemplateDialog} onOpenChange={setShowEmbedTemplateDialog}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="tiptap-embed-template-dialog">
          <DialogHeader>
            <DialogTitle>
              Insert {EMBED_TEMPLATES.find((t) => t.key === embedTemplateKind)?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="embed-template-input">
              {EMBED_TEMPLATES.find((t) => t.key === embedTemplateKind)?.inputLabel}
            </Label>
            <Input
              id="embed-template-input"
              value={embedTemplateValue}
              onChange={(e) => setEmbedTemplateValue(e.target.value)}
              placeholder={EMBED_TEMPLATES.find((t) => t.key === embedTemplateKind)?.placeholder}
              data-testid="tiptap-embed-template-input"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {EMBED_TEMPLATES.find((t) => t.key === embedTemplateKind)?.helper}
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowEmbedTemplateDialog(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={insertEmbedTemplate}
              disabled={!embedTemplateValue.trim()}
              className="rounded-none"
              data-testid="tiptap-embed-template-insert-btn"
            >
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HTML Embed Settings Dialog — resize + position (float) */}
      <Dialog open={showHtmlEmbedResize} onOpenChange={setShowHtmlEmbedResize}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="tiptap-html-embed-resize-dialog">
          <DialogHeader>
            <DialogTitle>HTML Embed Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="html-embed-width">Width (%)</Label>
              <div className="flex gap-2">
                <Input
                  id="html-embed-width"
                  type="number"
                  min="10"
                  max="100"
                  value={htmlEmbedWidth}
                  onChange={(e) => setHtmlEmbedWidth(e.target.value)}
                  className="rounded-none"
                  data-testid="tiptap-html-embed-width-input"
                />
                {["25", "50", "75", "100"].map((w) => (
                  <Button
                    key={w}
                    type="button"
                    variant="outline"
                    onClick={() => setHtmlEmbedWidth(w)}
                    className="rounded-none"
                    data-testid={`tiptap-html-embed-width-${w}`}
                  >
                    {w}%
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Or drag the embed's bottom-right corner in the editor to resize it.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Position</Label>
              <div className="flex gap-2">
                {[
                  { value: "none", label: "No wrap" },
                  { value: "left", label: "Float left" },
                  { value: "right", label: "Float right" },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={htmlEmbedFloat === opt.value ? "default" : "outline"}
                    onClick={() => setHtmlEmbedFloat(opt.value)}
                    className="rounded-none flex-1"
                    data-testid={`tiptap-html-embed-float-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Float left/right so body text wraps beside it — reduce the width first for the best result.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowHtmlEmbedResize(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={applyHtmlEmbedResize}
              className="rounded-none"
              data-testid="tiptap-html-embed-resize-apply"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table Properties Dialog — width, justification, bg/border colour+thickness */}
      <Dialog open={showTablePropsDialog} onOpenChange={setShowTablePropsDialog}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="tiptap-table-props-dialog">
          <DialogHeader>
            <DialogTitle>Table Properties</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Width</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={tableWidthMode === 'percent' ? 'default' : 'outline'}
                  onClick={() => setTableWidthMode('percent')}
                  className="rounded-none flex-1"
                  data-testid="tiptap-table-width-mode-percent"
                >
                  Percentage (%)
                </Button>
                <Button
                  type="button"
                  variant={tableWidthMode === 'fixed' ? 'default' : 'outline'}
                  onClick={() => setTableWidthMode('fixed')}
                  className="rounded-none flex-1"
                  data-testid="tiptap-table-width-mode-fixed"
                >
                  Fixed (px)
                </Button>
              </div>
              <Input
                type="number"
                min="1"
                value={tableWidthValue}
                onChange={(e) => setTableWidthValue(e.target.value)}
                className="rounded-none"
                data-testid="tiptap-table-width-value-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Justification</Label>
              <div className="flex gap-2">
                {[
                  { value: 'left', label: 'Left' },
                  { value: 'center', label: 'Center' },
                  { value: 'right', label: 'Right' },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={tableAlign === opt.value ? 'default' : 'outline'}
                    onClick={() => setTableAlign(opt.value)}
                    className="rounded-none flex-1"
                    data-testid={`tiptap-table-align-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            <TablePaletteSwatches
              label="Background Color"
              value={tableBgColor}
              onChange={setTableBgColor}
              testId="tiptap-table-bg-color"
            />
            <TablePaletteSwatches
              label="Border Color"
              value={tableBorderColor}
              onChange={setTableBorderColor}
              testId="tiptap-table-border-color"
            />

            <div className="space-y-2">
              <Label>Border Thickness (px)</Label>
              <div className="flex gap-2 flex-wrap">
                <Input
                  type="number"
                  min="0"
                  max="10"
                  step="0.5"
                  value={tableBorderWidth}
                  onChange={(e) => setTableBorderWidth(e.target.value)}
                  className="rounded-none w-20"
                  data-testid="tiptap-table-border-width-input"
                />
                {["0", "1", "2", "3", "4"].map((w) => (
                  <Button
                    key={w}
                    type="button"
                    variant="outline"
                    onClick={() => setTableBorderWidth(w)}
                    className="rounded-none"
                    data-testid={`tiptap-table-border-width-${w}`}
                  >
                    {w}px
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Cell Padding (px)</Label>
                <Input
                  type="number"
                  min="0"
                  max="60"
                  value={tableCellPadding}
                  onChange={(e) => setTableCellPadding(e.target.value)}
                  placeholder="Default"
                  className="rounded-none"
                  data-testid="tiptap-table-cell-padding-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Row Height (px)</Label>
                <Input
                  type="number"
                  min="0"
                  max="400"
                  value={tableCellHeight}
                  onChange={(e) => setTableCellHeight(e.target.value)}
                  placeholder="Auto"
                  className="rounded-none"
                  data-testid="tiptap-table-cell-height-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowTablePropsDialog(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={applyTableProperties}
              className="rounded-none"
              data-testid="tiptap-table-props-apply"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cell Alignment Dialog — justifies content inside the current cell,
          or every cell of a dragged multi-cell selection */}
      <Dialog open={showCellAlignDialog} onOpenChange={setShowCellAlignDialog}>
        <DialogContent className="sm:max-w-md rounded-none" data-testid="tiptap-cell-align-dialog">
          <DialogHeader>
            <DialogTitle>Cell Alignment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Horizontal</Label>
              <div className="flex gap-2">
                {[
                  { value: 'left', label: 'Left' },
                  { value: 'center', label: 'Centre' },
                  { value: 'right', label: 'Right' },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={cellAlign === opt.value ? 'default' : 'outline'}
                    onClick={() => setCellAlign(opt.value)}
                    className="rounded-none flex-1"
                    data-testid={`tiptap-cell-align-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Vertical</Label>
              <div className="flex gap-2">
                {[
                  { value: 'top', label: 'Top' },
                  { value: 'middle', label: 'Middle' },
                  { value: 'bottom', label: 'Bottom' },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={cellValign === opt.value ? 'default' : 'outline'}
                    onClick={() => setCellValign(opt.value)}
                    className="rounded-none flex-1"
                    data-testid={`tiptap-cell-valign-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Drag across several cells first to align them all at once, or
              just click into a single cell.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCellAlignDialog(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={applyCellAlignmentFromDialog}
              className="rounded-none"
              data-testid="tiptap-cell-align-apply"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Resize Dialog */}
      <Dialog open={showImageResize} onOpenChange={(v) => !v && closeImageResizeDialog()}>
        <DialogContent className="sm:max-w-md rounded-none max-h-[85vh] overflow-y-auto" data-testid="tiptap-image-resize-dialog">
          <DialogHeader>
            <DialogTitle>Resize &amp; Adjust Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="image-width">Width (%)</Label>
              <div className="flex gap-2">
                <Input
                  id="image-width"
                  type="number"
                  min="10"
                  max="100"
                  value={imageWidth}
                  onChange={(e) => setImageWidth(e.target.value)}
                  className="rounded-none"
                  data-testid="tiptap-image-width-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setImageWidth('50')}
                  className="rounded-none"
                  data-testid="tiptap-image-width-50"
                >
                  50%
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setImageWidth('75')}
                  className="rounded-none"
                >
                  75%
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setImageWidth('100')}
                  className="rounded-none"
                >
                  100%
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Click an image in the editor to resize it (10-100%)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="image-height">Height (px) — optional</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="image-height"
                  type="number"
                  min="1"
                  placeholder="Auto"
                  value={imageHeightPx}
                  onChange={(e) => setImageHeightPx(e.target.value)}
                  className="rounded-none"
                  data-testid="tiptap-image-height-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setImageHeightPx('')}
                  className="rounded-none shrink-0"
                  data-testid="tiptap-image-height-clear"
                >
                  Auto
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Set an exact height and the width adjusts automatically to
                keep the image's proportions — overrides the Width % above.
                Leave blank to size by Width instead.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="image-opacity">Opacity (%)</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="image-opacity"
                  type="range"
                  min="10"
                  max="100"
                  step="5"
                  value={imageOpacity}
                  onChange={(e) => setImageOpacity(e.target.value)}
                  className="flex-1"
                />
                <Input
                  type="number"
                  min="10"
                  max="100"
                  value={imageOpacity}
                  onChange={(e) => setImageOpacity(e.target.value)}
                  className="rounded-none w-20"
                  data-testid="tiptap-image-opacity-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Adjust transparency (10% = very transparent, 100% = fully opaque)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Text wrap</Label>
              <div className="flex gap-2">
                {[
                  { value: "none", label: "No wrap" },
                  { value: "left", label: "Float left" },
                  { value: "right", label: "Float right" },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={imageFloat === opt.value ? "default" : "outline"}
                    onClick={() => setImageFloat(opt.value)}
                    className="rounded-none flex-1"
                    data-testid={`tiptap-image-float-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Float left/right so body text wraps beside the image — reduce the width first for the best result.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Horizontal justification</Label>
              <div className="flex gap-2">
                {[
                  { value: "left", label: "Left" },
                  { value: "center", label: "Centre" },
                  { value: "right", label: "Right" },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={imageJustify === opt.value ? "default" : "outline"}
                    onClick={() => setImageJustify(imageJustify === opt.value ? "none" : opt.value)}
                    className="rounded-none flex-1"
                    data-testid={`tiptap-image-justify-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Positions the image as its own block (only applies when Text wrap is "No wrap").
              </p>
            </div>

            <div className="space-y-2">
              <Label>Vertical alignment</Label>
              <div className="flex gap-2">
                {[
                  { value: "top", label: "Top" },
                  { value: "middle", label: "Middle" },
                  { value: "bottom", label: "Bottom" },
                ].map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    variant={imageValign === opt.value ? "default" : "outline"}
                    onClick={() => setImageValign(imageValign === opt.value ? "none" : opt.value)}
                    className="rounded-none flex-1"
                    data-testid={`tiptap-image-valign-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Lines the image up with surrounding inline text at its top, middle or bottom.
              </p>
            </div>
            <ImagePresetsRow
              radius={parseInt(imageRadius, 10) || 0}
              shadow={imageShadow}
              onApply={(r, s) => { setImageRadius(String(r)); setImageShadow(s); }}
              testIdPrefix="tiptap-image-preset"
            />
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Corner Radius</Label>
                <span className="text-xs text-muted-foreground" data-testid="tiptap-image-radius-value">{imageRadius}px</span>
              </div>
              <Slider
                value={[parseInt(imageRadius, 10) || 0]} min={0} max={80} step={1}
                onValueChange={([v]) => setImageRadius(String(v))}
                data-testid="tiptap-image-radius-slider"
              />
            </div>

            <div className="space-y-2">
              <Label>Shadow</Label>
              <div className="flex gap-2">
                {Object.keys(IMAGE_SHADOW_PRESETS).map((key) => (
                  <Button
                    key={key}
                    type="button"
                    variant={imageShadow === key ? "default" : "outline"}
                    onClick={() => setImageShadow(key)}
                    className="rounded-none capitalize flex-1"
                    data-testid={`tiptap-image-shadow-${key}`}
                  >
                    {key}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Blur</Label>
                <span className="text-xs text-muted-foreground" data-testid="tiptap-image-blur-value">{imageBlur}px</span>
              </div>
              <Slider
                value={[parseInt(imageBlur, 10) || 0]} min={0} max={20} step={1}
                onValueChange={([v]) => setImageBlur(String(v))}
                data-testid="tiptap-image-blur-slider"
              />
              <p className="text-xs text-muted-foreground">
                De-emphasise a background-style photo, e.g. behind overlay text.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Brightness</Label>
                <span className="text-xs text-muted-foreground" data-testid="tiptap-image-brightness-value">{imageBrightness}%</span>
              </div>
              <Slider
                value={[parseInt(imageBrightness, 10) || 100]} min={30} max={200} step={5}
                onValueChange={([v]) => setImageBrightness(String(v))}
                data-testid="tiptap-image-brightness-slider"
              />
            </div>

            <div className="space-y-2">
              <Label>Gradient overlay</Label>
              <div className="flex gap-1.5 flex-wrap">
                {GRADIENT_DIRECTIONS.map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    size="sm"
                    variant={imageGradient === opt.value ? "default" : "outline"}
                    onClick={() => setImageGradient(opt.value)}
                    className="rounded-none text-xs"
                    data-testid={`tiptap-image-gradient-${opt.value}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
              {imageGradient !== 'none' && (
                <>
                  <TablePaletteSwatches
                    label="Colour"
                    value={imageGradientColor}
                    onChange={setImageGradientColor}
                    testId="tiptap-image-gradient-color"
                  />
                  <div className="flex items-center justify-between pt-1">
                    <Label>Strength</Label>
                    <span className="text-xs text-muted-foreground" data-testid="tiptap-image-gradient-opacity-value">{imageGradientOpacity}%</span>
                  </div>
                  <Slider
                    value={[parseInt(imageGradientOpacity, 10) || 60]} min={0} max={100} step={5}
                    onValueChange={([v]) => setImageGradientOpacity(String(v))}
                    data-testid="tiptap-image-gradient-opacity-slider"
                  />
                </>
              )}
              <p className="text-xs text-muted-foreground">
                Fades toward a colour so overlaid text stays legible.
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full rounded-none"
              onClick={() => setShowImageCrop(true)}
              data-testid="tiptap-image-crop-btn"
            >
              <CropIcon className="h-4 w-4 mr-2" />
              Crop / Rotate / Flip...
            </Button>
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={closeImageResizeDialog}
              className="rounded-none"
              data-testid="tiptap-image-resize-apply"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageEditorModal
        open={showImageCrop}
        onClose={() => setShowImageCrop(false)}
        sourceUrl={selectedImageSrc}
        onSaved={handleImageCropSaved}
      />
    </div>
  );
}

export default TiptapEditor;
